import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { selectedQuote } from './FinancingGridCard';
import CurrencyInput from 'react-currency-input-field';
import {
    batteryCalculatorC4FromColumn,
    batterySvaC4,
    columnIncludesBattery,
    columnIncludesSolar,
    svaProductCodeFromColumn,
} from './quoteCreationProducts';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { getEnfinEscalatorRateFromPriceQuote } from './enfinEligibleProductUtils';
import { GenerateQuoteSectionHead } from './GenerateQuoteSectionHead';
import type { GqSectionStat } from './GenerateQuoteSectionHead.types';
import './oneButton-css/RoofQuoteDetails.css';
import { Slider } from 'antd';
import { OBDSelect } from './form-elements';
function positiveOfferNumber(value: unknown): number | null {
    const n =
        typeof value === 'number'
            ? value
            : Number(String(value ?? '').replace(/[$,]/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

function pricingMethodLabel(text: unknown): string {
    return String(text ?? '').trim().toLowerCase();
}

function isSolarRatePricingMethod(text: unknown): boolean {
    const method = pricingMethodLabel(text);
    return method === 'solar rate' || method === 'rate';
}

function isPpwPricingMethod(text: unknown): boolean {
    return pricingMethodLabel(text) === 'ppw';
}

/** PCI stores 0.0299; Trinity Escalator options are 2.99 / 1.99 / 0. */
function matchQuoteEscalatorOption(
    raw: unknown,
    options: Array<{ Value?: string }>,
): string | undefined {
    if (raw == null || String(raw).trim() === '') return undefined;
    const numeric = Number(String(raw).replace(/%/g, '').trim());
    if (!Number.isFinite(numeric)) {
        return options.find((option) => String(option.Value).trim() === String(raw).trim())?.Value;
    }
    const asPercent = numeric > 0 && numeric <= 1 ? numeric * 100 : numeric;
    const match = options.find((option) => {
        const optionNumeric = Number(String(option.Value ?? '').replace(/%/g, '').trim());
        if (!Number.isFinite(optionNumeric)) return false;
        return Math.abs(optionNumeric - asPercent) < 1e-3 || Math.abs(optionNumeric - numeric) < 1e-6;
    });
    return match?.Value ?? String(asPercent);
}

/** Solar Rate → Target Rate (`startingRate`); PPW → Target PPW (`startingPPW`). */
function defaultPriceForPricingMethod(
    methodText: unknown,
    excelOffer: {
        startingRate?: unknown;
        StartingRate?: unknown;
        startingPPW?: unknown;
        StartingPPW?: unknown;
    } | null | undefined,
    fallback: number,
): number {
    if (isSolarRatePricingMethod(methodText)) {
        return (
            positiveOfferNumber(excelOffer?.startingRate) ??
            positiveOfferNumber(excelOffer?.StartingRate) ??
            fallback
        );
    }
    if (isPpwPricingMethod(methodText)) {
        return (
            positiveOfferNumber(excelOffer?.startingPPW) ??
            positiveOfferNumber(excelOffer?.StartingPPW) ??
            fallback
        );
    }
    return fallback;
}

export interface SolarQuoteFormState {
    financeMethod: string;
    DesiredPPWNet: string;
    Deposit_Amount: string;
    UponCommencementOfPhysicalInstall: string;
    UponCompletionOfPhysicalInstall: string;
    UponCompletionOfTheContract: string;
}

export interface DropdownItem {
    Value: string | number;
    Text: string;
    PricingValue?: string | number;
}

export interface FieldObject {
    label: string;
    name: string;
    type: string;
    value: any;
    enable: boolean;
    errorMessage: string;
    message?: string | null;
    field?: string;
    dataType?: string;
    haveAccess?: boolean;
    options?: DropdownItem[];
    range?: {
        nzMin: number;
        nzMax: number;
        nzValue?: number;
        nzStep?: number;
        nzMarks: Record<string, string | { label?: string; style?: Record<string, string> }>;
        nzTooltipVisible: string;
        style?: string;
        targetRate?: number;
        escalationRate?: string | number;
    };
}

export interface ApiResponse {
    OutputValues: { CellValue: any }[];
    [key: string]: any;
}

/** Default solar quote form rows (cloned into component state; options/enable flags are filled in from Excel/API). */
export const solarFields: FieldObject[] = [
    {
        label: 'Term & APR',
        name: 'TermAPR',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Pricing Method',
        name: 'PricingMethod',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Pricing Value',
        name: 'Price',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Escalator',
        name: 'Escalator',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Rate',
        name: 'Rate',
        type: 'textbox',
        value: '',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'EPC',
        name: 'EPC',
        type: 'textbox',
        value: '',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'PPW',
        name: 'PPW',
        type: 'textbox',
        value: '',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Down Payment',
        name: 'DownPayment',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Desired PPW (Net)',
        name: 'DesiredPPWNet',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Deposit Amount',
        name: 'Deposit_Amount',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Rebate Payment Type',
        name: 'RebateMethod',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Rebate Value',
        name: 'RebateValue',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Rebate Amount Per Watt',
        name: 'RebateAmountPerWatt',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },

    {
        label: 'Tax',
        name: 'Tax',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Adder Cost',
        name: 'AdderCost',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Adder Price',
        name: 'AdderPrice',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Upon Commencement of Physical Install',
        name: 'UponCommencementOfPhysicalInstall',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Upon Completion of Physical Install',
        name: 'UponCompletionOfPhysicalInstall',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Upon Completion of the Contract',
        name: 'UponCompletionOfTheContract',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    }
];

/** Default fraction digits for currency-style amounts (PPW, dollars, etc.). */
const DEFAULT_AMOUNT_DECIMALS_LIMIT = 2;

/**
 * Pricing method option labels (normalized lowercase) where Price represents an energy rate ($/kWh).
 * Three decimals align with typical utility / Solar Rate tariff quoting precision.
 */
const PRICING_METHOD_LABELS_EXTRA_PRICE_DECIMALS: readonly string[] = ['rate', 'solar rate'];

export const SunnovaLoanOptions = ['Loan', 'Loan Storage', 'Lease Storage']

/** Cash-only payment schedule fields (Angular `updateAccess`). */
const CASH_ONLY_FIELD_NAMES: readonly string[] = [
    'UponCommencementOfPhysicalInstall',
    'UponCompletionOfPhysicalInstall',
    'UponCompletionOfTheContract',
];

/** Maps Excel SVA `financialArr` tokens to `solarFields` `name` (Angular `updateAccess`). */
const resolveSolarFieldNameFromFinancialToken = (token: string): string | null => {
    const normalized = token.trim().toLowerCase();
    if (!normalized) return null;
    if (normalized === 'term&apr') return 'TermAPR';
    if (normalized === 'deposit') return 'Deposit_Amount';
    if (normalized === 'rebate method' || normalized === 'rebate payment type') return 'RebateMethod';
    const direct = solarFields.find(
        (f) =>
            f.name.toLowerCase() === normalized ||
            f.label.trim().toLowerCase() === normalized,
    );
    return direct?.name ?? null;
};

const ENFIN_INELIGIBLE_PRICE_MSG =
    'The selected pricing is not available from Enfin. Please click the "Enfin Eligible Product" button below to review all valid pricing available from Enfin.';

const REBATE_PER_WATT_VALIDATION_MSG =
    'The entered Rebate amount per watt is wrong (it will be upto $1 and less than the Desired PPW Net value)';

/** Angular `RebateAmountPerWattValidator` — applies when rebate method is Per Watt. */
function getRebatePerWattValidationMessage(
    rebateMethod: string | undefined,
    rebateVal: number,
    desiredPpw: number,
): string {
    if (rebateMethod !== 'Per Watt' || rebateVal <= 0) return '';
    if (rebateVal > 1 || (desiredPpw > 0 && rebateVal >= desiredPpw)) {
        return REBATE_PER_WATT_VALIDATION_MSG;
    }
    return '';
}

/**
 * Resolves the rebate amount used in Excel totals and save payload.
 * Angular reads `RebateValue` for all paths; when only `RebateAmountPerWatt` is enabled from Excel, use that field.
 */
function getRebateEntryValue(currentFields: FieldObject[], method?: string): string | number {
    const rebateMethod = method ?? getFieldDetailsFromFields(currentFields, 'RebateMethod')?.value;
    const rebateValueField = getFieldDetailsFromFields(currentFields, 'RebateValue');
    const rebatePerWattField = getFieldDetailsFromFields(currentFields, 'RebateAmountPerWatt');

    if (rebateMethod === 'Per Watt') {
        if (rebatePerWattField?.enable && !rebateValueField?.enable) {
            return rebatePerWattField?.value ?? 0;
        }
        return rebateValueField?.value ?? rebatePerWattField?.value ?? 0;
    }
    return rebateValueField?.value ?? 0;
}

function getFieldDetailsFromFields(fields: FieldObject[], name: string): FieldObject | undefined {
    return fields.find((item) => item.name === name);
}

type EnfinDerived = {
    enfinTotalSaleAmount: number;
    enfinGrossSalesPrice: number;
    enfinSolarSalesPrice: number;
    enfinMaxTotalSalesPrice: number;
    enfinMatchedPricingOffer: Record<string, unknown> | null;
    inEligibleEnfinQuote: boolean;
    noEligibleEnfinProducts: boolean;
    noEligibleEnfinSolarBatteryProducts: boolean;
    enfinGrossSalesPriceExceedsRange: boolean;
    priceValidationMessage: string;
    formErrorMessage: string;
};

/** Totals Angular keeps on the component instance rather than recomputing per call. */
type EnfinRetainedTotals = Pick<
    EnfinDerived,
    'enfinTotalSaleAmount' | 'enfinGrossSalesPrice' | 'enfinSolarSalesPrice' | 'enfinMatchedPricingOffer'
>;

/** Parity with Angular `solar-detail-form.component.ts` `validateEnfinQuote`. */
function enfinOfferDetails(raw: unknown): Record<string, unknown>[] {
    if (!raw || typeof raw !== 'object') return [];
    const details =
        (raw as Record<string, unknown>).pricingOfferDetails ??
        (raw as Record<string, unknown>).PricingOfferDetails;
    return Array.isArray(details) ? (details as Record<string, unknown>[]) : [];
}

function deriveEnfinValidation(args: {
    isEnfin: boolean;
    enfinPricingLoaded: boolean;
    enfinPricingOfferDetails: Record<string, unknown>[];
    enfinBatteryPricingOfferDetails: Record<string, unknown>[];
    enfinBatteryPricingLoaded: boolean;
    pricingMethodText: string;
    priceRaw: unknown;
    productTypeCode: string;
    batteryTotalAmount: number;
    /** Angular's `validateEnfinQuote` returns before touching the instance totals while pricing is still
     *  loading, so the last matched offer must survive instead of collapsing to 0. */
    retainedTotals?: EnfinRetainedTotals;
}): EnfinDerived {
    const empty: EnfinDerived = {
        enfinTotalSaleAmount: 0,
        enfinGrossSalesPrice: 0,
        enfinSolarSalesPrice: 0,
        enfinMaxTotalSalesPrice: 0,
        enfinMatchedPricingOffer: null,
        inEligibleEnfinQuote: false,
        noEligibleEnfinProducts: false,
        noEligibleEnfinSolarBatteryProducts: false,
        enfinGrossSalesPriceExceedsRange: false,
        priceValidationMessage: '',
        formErrorMessage: '',
    };
    if (!args.isEnfin) return empty;
    if (!args.enfinPricingLoaded) return { ...empty, ...(args.retainedTotals ?? {}) };
    if (!args.enfinPricingOfferDetails?.length) {
        return {
            ...empty,
            noEligibleEnfinProducts: true,
            formErrorMessage:
                'No eligible products found. Please update the Utility, System Design, and try again.',
        };
    }
    const pricingLabel = (args.pricingMethodText || '').trim().toLowerCase();
    if (!['solar rate', 'rate'].includes(pricingLabel)) return empty;

    const matchedProduct = args.enfinPricingOfferDetails.find(
        (product) =>
            Number(product?.totalkwhrate).toFixed(3) === Number(args.priceRaw).toFixed(3),
    );

    if (matchedProduct) {
        const totalSalesPrice = Number(matchedProduct.TotalSalesPrice) || 0;
        const isSb = args.productTypeCode === 'SB';
        const enfinSolarSalesPrice = isSb ? totalSalesPrice : 0;
        const enfinGrossSalesPrice = isSb
            ? totalSalesPrice + Number(args.batteryTotalAmount || 0)
            : totalSalesPrice;

        let enfinMaxTotalSalesPrice = 0;
        let enfinGrossSalesPriceExceedsRange = false;
        if (isSb) {
            enfinMaxTotalSalesPrice = args.enfinBatteryPricingOfferDetails.reduce((maxPrice, product) => {
                const total = Number(product?.TotalSalesPrice);
                return Number.isNaN(total) ? maxPrice : Math.max(maxPrice, total);
            }, 0);
            enfinGrossSalesPriceExceedsRange =
                enfinMaxTotalSalesPrice > 0 && enfinGrossSalesPrice > enfinMaxTotalSalesPrice;
        }

        if (isSb && args.enfinBatteryPricingLoaded && !args.enfinBatteryPricingOfferDetails?.length) {
            return {
                enfinTotalSaleAmount: totalSalesPrice,
                enfinGrossSalesPrice,
                enfinSolarSalesPrice,
                enfinMaxTotalSalesPrice,
                enfinMatchedPricingOffer: matchedProduct,
                inEligibleEnfinQuote: false,
                noEligibleEnfinProducts: false,
                noEligibleEnfinSolarBatteryProducts: true,
                enfinGrossSalesPriceExceedsRange: false,
                priceValidationMessage: '',
                formErrorMessage: 'No eligible products available for the Solar + Battery combination.',
            };
        }

        if (enfinGrossSalesPriceExceedsRange) {
            return {
                enfinTotalSaleAmount: totalSalesPrice,
                enfinGrossSalesPrice,
                enfinSolarSalesPrice,
                enfinMaxTotalSalesPrice,
                enfinMatchedPricingOffer: matchedProduct,
                inEligibleEnfinQuote: false,
                noEligibleEnfinProducts: false,
                noEligibleEnfinSolarBatteryProducts: false,
                enfinGrossSalesPriceExceedsRange: true,
                priceValidationMessage: '',
                formErrorMessage: `Gross sales price exceeds the allowed range for Solar + Battery. Maximum allowed: $${enfinMaxTotalSalesPrice.toFixed(2)}`,
            };
        }

        return {
            enfinTotalSaleAmount: totalSalesPrice,
            enfinGrossSalesPrice,
            enfinSolarSalesPrice,
            enfinMaxTotalSalesPrice,
            enfinMatchedPricingOffer: matchedProduct,
            inEligibleEnfinQuote: false,
            noEligibleEnfinProducts: false,
            noEligibleEnfinSolarBatteryProducts: false,
            enfinGrossSalesPriceExceedsRange: false,
            priceValidationMessage: '',
            formErrorMessage: '',
        };
    }

    return {
        ...empty,
        inEligibleEnfinQuote: true,
        priceValidationMessage: ENFIN_INELIGIBLE_PRICE_MSG,
    };
}

interface SolarQuoteDetailsProps {
    confirmQuoteCellKey: selectedQuote | null;
    priceQuote: any;
    theme: string;
    lineMode?: 'solar' | 'battery';
    lightreachEligibleProductData?: any[];
    /** False while Generate Quote prefetches Lightreach pricing rows — avoid marking ineligible on empty list. */
    lightreachEligibleProductsReady?: boolean;
    lightreachPriceCapsData?: any[];
    /** Solar + Battery: battery line total for Enfin gross sales validation (`productType == 'SB'`). */
    batteryTotalAmount?: number;
    updateLoaderCount: (loader: boolean) => void;
    updateErrorMessage: (message: string) => void;
    updateSolarDetails: (data: SolarDetails) => void;

}

export interface SolarDetails {
    opportunity_id: string;
    financePartnerName: string;
    financePartnerId?: number;
    contract_Type: string;
    epc: number;
    rate: number;
    ppw: number;
    pricingmethodName: string;
    pricingmethod: string | number;
    pricingValue: number;
    rebateMethod: string;
    rebate: number;
    Escalator: number;
    downPayment: number;
    tax: number;
    TermAndAPR: string;
    design: string;
    PermitPayer: string;
    TotalSaleAmount: number;
    DesiredPPWNet: number;
    RebateAmountPerWatt: number;
    AdderPrice: number;
    AdderCost: number;
    valid: boolean;
    validationMessage?: string;
    grossSalesPricePerWatt: number;
    dealerFeePercentage: number;
    dealerEpc: number;
    Monthly_Finance_Payment_NoITCPayback: number;
    Monthly_Finance_Partner_Payment: number;
    ITC_Amount: number;
    Finance_Partner_Product_Id: string;
    Offer_Id: string;
    IsACH: string;
    Product_Type: string;
    productionRate: number;
    SolarProductionRate: number;
    Deposit_Amount: number;
    Financed_Amount: number;
    Total_Amount: number;
    UponCommencementOfPhysicalInstall: any;
    UponCompletionOfPhysicalInstall: any;
    UponCompletionOfTheContract: any;
    SyncProductInfo?: any
}

/**
 * Solar (or battery line) quote form: binds dynamic fields to Excel-backed pricing (cascadingDropDown)
 * and pushes a consolidated payload to the parent via `updateSolarDetails`.
 * Partner-specific branches (GoodLeap, Sunlight, IGS, etc.) override how totals and PPW are resolved.
 */
export const SolarQuoteDetails: React.FC<SolarQuoteDetailsProps> = ({
    confirmQuoteCellKey,
    priceQuote,
    theme,
    lineMode = 'solar',
    lightreachEligibleProductData = [],
    lightreachEligibleProductsReady = true,
    lightreachPriceCapsData: _lightreachPriceCapsData = [],
    batteryTotalAmount = 0,
    updateLoaderCount,
    updateErrorMessage,
    updateSolarDetails
}) => {
    
    const [solarQuoteFormFields, setsolarQuoteFormFields] = useState<FieldObject[]>(JSON.parse(JSON.stringify(solarFields)));
    const [formHydrating, setFormHydrating] = useState(true);
    const hydratingLoaderRef = useRef(false);
    const [financeMethod, setFinanceMethod] = useState<string>('');
    const [eligibleProduct, setEligibleProduct] = useState<any[]>([]);

    const [pricingMethodArr, setPricingMethodArr] = useState<DropdownItem[]>([]);
    const [rebateMethods, setRebateMethods] = useState<DropdownItem[]>([]);
    const [escalatorArr, setEscalatorArr] = useState<DropdownItem[]>([]);
    const [termAPRArr, setTermAPRArr] = useState<DropdownItem[]>([]);
    const [solarFinanceMethods, setSolarFinanceMethods] = useState<DropdownItem[]>([]);
    const [downPaymentList, setDownPaymentList] = useState<DropdownItem[]>([]);
    const [financialArr, setFinancialArr] = useState<string[]>([]);
    const [Finance_Partner_Product_Id, setFinance_Partner_Product_Id] = useState('');
    const [Offer_Id, setOffer_Id] = useState('');
    const [existingOfferIdDetails, setExistingOfferIdDetails] = useState<any>(null);

    const [totalSaleAmount, setTotalSaleAmount] = useState(0);
    const [PPW, setPPW] = useState<any>(null);

    const [IsACH, setIsACH] = useState('');
    const [ProductType, setProductType] = useState('');

    const [productionRate, setProductionRate] = useState(0);

    const [financedAmount, setFinancedAmount] = useState(0);
    const [rebateAmount, setRebateAmount] = useState(0);
    const [grossSalesPricePerWatt, setGrossSalesPricePerWatt] = useState(0);
    const [dealerFeePercentage, setDealerFeePercentage] = useState(0);
    const [dealerEpc, setDealerEpc] = useState<any>();
    const [Monthly_Finance_Payment_NoITCPayback, setMonthly_Finance_Payment_NoITCPayback] = useState(0);
    const [ITCAmount, setITCAmount] = useState(0);
    const [Monthly_Finance_Partner_Payment, setMonthly_Finance_Partner_Payment] = useState(0);

    const [autoQuoteCreationInitiated, setAutoQuoteCreationInitiated] = useState(false);
    const [goodLeapValidationErrorMessage, setGoodLeapValidationErrorMessage] = useState('');

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const storeOpportunityId = useOneButtonStore((s) => s.opportunityID);
    const storeSystemDesign = useOneButtonStore((s) => s.systemDesign);

    /** Latest outputs from the Solar Calculator Excel cascade (totals, PPW, payments). Used when form fields lag or partner logic needs computed values. */
    const salesAmoontDetailsRef = React.useRef<any>({});
    /** Last string from CurrencyInput `onValueChange` per field — blur runs totals with this so we never read stale state before re-render. */
    const amountDraftRef = React.useRef<Record<string, string>>({});
    /** Excel SVA Financing Options payload — used by Lightreach slider target rate (Angular `pricingDetailsFromExcel`). */
    const pricingDetailsFromExcelRef = React.useRef<any>(null);
    const haveValidationErrors = (currentFields: FieldObject[]) => {
        return currentFields.find((item: any) => (item.enable || item.haveAccess) && item.message);
    }

    const VALIDATION_MESSAGE_SEP = '\n';

    const collectSolarValidationMessages = (
        currentFields: FieldObject[],
        enfin: EnfinDerived,
        lightreachFormInvalid: boolean,
        lightreachPriceValidationMessage: string,
        enfinFormErrorMessage?: string,
    ): string[] => {
        const messages: string[] = [];
        const addMessage = (message?: string) => {
            const trimmed = String(message ?? '').trim();
            if (trimmed && !messages.includes(trimmed)) {
                messages.push(trimmed);
            }
        };

        if (isEnfinPartner) {
            addMessage(enfin.formErrorMessage);
            addMessage(enfinFormErrorMessage);
        }
        if (lightreachFormInvalid) {
            addMessage(lightreachPriceValidationMessage);
        }
        currentFields.forEach((item) => {
            if (item.message) {
                addMessage(String(item.message));
            }
        });
        addMessage(goodLeapValidationErrorMessage);

        return messages;
    };

    /** Reads a field from `solarQuoteFormFields`. Pass `currentFields` when you just mutated a copy and React state has not re-rendered yet. */
    const getFieldDetails = (field: string, currentFields?: FieldObject[]) => {
        return (currentFields || solarQuoteFormFields).find((item: any) => item.name === field);
    }

    const cloneFormFields = (fields: FieldObject[]): FieldObject[] =>
        fields.map((f) => ({
            ...f,
            ...(f.range ? { range: { ...f.range } } : {}),
        }));

    useEffect(() => {
        if (lineMode === 'battery') return;
        if (formHydrating) {
            if (!hydratingLoaderRef.current) {
                hydratingLoaderRef.current = true;
                updateLoaderCount(true);
            }
            return;
        }
        if (hydratingLoaderRef.current) {
            hydratingLoaderRef.current = false;
            updateLoaderCount(false);
        }
    }, [formHydrating, lineMode, updateLoaderCount]);

    useEffect(
        () => () => {
            if (hydratingLoaderRef.current) {
                hydratingLoaderRef.current = false;
                updateLoaderCount(false);
            }
        },
        [updateLoaderCount],
    );

    const partnerNorm = (confirmQuoteCellKey?.partner || '').replace(/\s+/g, '').toLowerCase();
    const isLightreachPartner = partnerNorm === 'lightreach';
    const isEnfinPartner = partnerNorm === 'enfin';
    const [enfinPricingOfferDetails, setEnfinPricingOfferDetails] = useState<Record<string, unknown>[]>([]);
    const [enfinBatteryPricingOfferDetails, setEnfinBatteryPricingOfferDetails] = useState<Record<string, unknown>[]>([]);
    const [enfinPricingLoaded, setEnfinPricingLoaded] = useState(false);
    const [enfinBatteryPricingLoaded, setEnfinBatteryPricingLoaded] = useState(false);
    /** True while `getSolarQuoteFormValues` is chaining GetEnfinPricing after Excel init (single loader cycle). */
    const enfinInitLoadRef = useRef(false);
    const enfinPricingFetchStartedRef = useRef(false);
    const enfinPricingLoadGenRef = useRef(0);
    /** Ensures only one loader +1 is active across overlapping GetEnfinPricing calls. */
    const enfinLoaderIncrementedRef = useRef(false);
    const enfinEmptyForceRef = useRef(false);
    /** During Excel init, `setSolarQuoteFromValues` mutates this instead of stale React state. */
    const initFormFieldsRef = useRef<FieldObject[] | null>(null);
    /** Prevents overlapping Excel total-sale requests and feedback loops from field sync. */
    const updateTotalSaleAmountInFlightRef = useRef(false);

    const syncPriceFieldsAfterExcelTotal = (
        fields: FieldObject[],
        pricingValue: { Text?: string } | null | undefined,
    ): FieldObject[] => {
        if (
            pricingValue?.Text !== 'Desired PPW (Net)' &&
            pricingValue?.Text !== 'EPC' &&
            !(confirmQuoteCellKey?.partner == 'Cash' && !confirmQuoteCellKey?.clonedQuoteDetails)
        ) {
            return fields;
        }
        if (
            getFieldDetails('DesiredPPWNet', fields)?.value != getFieldDetails('Price', fields)?.value &&
            getFieldDetails('Price', fields)?.enable
        ) {
            return updateSolarQuoteFormFieldsTemp(
                fields,
                'DesiredPPWNet',
                getFieldDetails('Price', fields)?.value,
            );
        }
        if (
            !getFieldDetails('DesiredPPWNet', fields)?.value ||
            getFieldDetails('DesiredPPWNet', fields)?.value == 0
        ) {
            if (confirmQuoteCellKey?.ExcelOffer?.startingPPW) {
                const startingPPW = confirmQuoteCellKey.ExcelOffer.startingPPW;
                let next = updateSolarQuoteFormFieldsTemp(fields, 'Price', startingPPW);
                next = updateSolarQuoteFormFieldsTemp(next, 'DesiredPPWNet', startingPPW);
                return next;
            }
        }
        return fields;
    };
    const isGoodLeap = useMemo(
        () => (confirmQuoteCellKey?.partner || '').replace(/\s+/g, '').toLowerCase() === 'goodleap',
        [confirmQuoteCellKey?.partner],
    );

    /**
 * Parity with Angular `solar-detail-form.component.ts` (`validateLightreachQuote` and `updateSolarDetails`
 * for LightReach: `eligibleLightreachProductId` / `lightreachTotalSaleAmount` → `Offer_Id` / `TotalSaleAmount`).
 */
    function lrKwhRatesEqual(a: unknown, b: unknown): boolean {
        const na = Number(a);
        const nb = Number(b);
        if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
        return Math.abs(na - nb) < 0.000001;
    }

    function lrContractTypesEqual(productType: unknown, contractType: string): boolean {
        return String(productType ?? '').trim().toLowerCase() === contractType.trim().toLowerCase();
    }

    function normalizeLightreachEscalatorRate(escalatorRaw: unknown): number {
        const n = Number(escalatorRaw);
        if (!Number.isFinite(n)) return NaN;
        const asDecimal = n > 1 ? n / 100 : n;
        return Math.round(asDecimal * 10000) / 10000;
    }

    function lrEscalationRatesEqual(a: unknown, b: number): boolean {
        const na = normalizeLightreachEscalatorRate(a);
        const nb = Number(b);
        if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
        return Math.abs(na - nb) < 0.000001;
    }

    /** V1 `FinanceMethodId` (PPA / Lease), not full pricing-method label from the grid. */
    function resolveQuoteLseId(pq: Record<string, unknown> | null | undefined): number {
        if (!pq) return NaN;
        const raw =
            pq.LSEDID ??
            pq.LseId ??
            pq.LSEID ??
            pq.lseId ??
            pq.Utility_LSE_Id ??
            pq.utility_LSE_Id;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    }

    function lightreachProductLseId(row: Record<string, unknown>): number {
        const n = Number(row.LseId ?? row.LSEID ?? row.lseId);
        return Number.isFinite(n) ? n : NaN;
    }

    /** Solar rows only — ignore merged battery `energyBackup` offers when validating solar rate. */
    function lightreachSolarEligibleProducts(products: any[]): any[] {
        const list = Array.isArray(products) ? products : [];
        const solarOnly = list.filter((p) => {
            const program = String(p?.ProgramType ?? p?.programType ?? 'solar').toLowerCase();
            return program === 'solar' || program === '';
        });
        return solarOnly.length > 0 ? solarOnly : list;
    }

    function resolveLightreachContractType(financeMethod: string, gridMethod: string): string {
        const pick = (raw: string) => {
            const t = raw.trim().toLowerCase();
            if (!t) return '';
            if (t === 'ppa' || t.startsWith('ppa-') || t.startsWith('ppa ')) return 'ppa';
            if (t.includes('lease')) return 'lease';
            return (t.split(/[\s-/]+/)[0] ?? t).trim();
        };
        return pick(financeMethod) || pick(gridMethod);
    }

    /** Parity with V1 `validateLightreachQuote` product match + base EPC (adders netted separately). */
    function deriveLightreachOfferAndTotal(args: {
        isLightreach: boolean;
        products: any[];
        eligibleProductsReady?: boolean;
        quoteLseIdRaw: any;
        financeMethod: string;
        gridMethord: string;
        priceRaw: any;
        escalatorRaw: any;
        totalAdders?: number;
        preferredProductId?: string;
    }): {
        eligibleLightreachProductId: string;
        lightreachTotalSaleAmount: number;
        inEligibleLightreachQuote: boolean;
        priceValidationMessage: string;
    } {
        const LIGHTREACH_PRICE_MSG =
            'Pricing chosen is not available from Lightreach, please click on the "LightReach Eligible Products" button below to review all the valid pricing available from Lightreach.';
        if (!args.isLightreach) {
            return {
                eligibleLightreachProductId: '',
                lightreachTotalSaleAmount: 0,
                inEligibleLightreachQuote: false,
                priceValidationMessage: '',
            };
        }
        const products = lightreachSolarEligibleProducts(Array.isArray(args.products) ? args.products : []);
        if (products.length === 0) {
            if (args.eligibleProductsReady === false) {
                return {
                    eligibleLightreachProductId: '',
                    lightreachTotalSaleAmount: 0,
                    inEligibleLightreachQuote: false,
                    priceValidationMessage: '',
                };
            }
            return {
                eligibleLightreachProductId: '',
                lightreachTotalSaleAmount: 0,
                inEligibleLightreachQuote: true,
                priceValidationMessage: LIGHTREACH_PRICE_MSG,
            };
        }
        const quoteLseId = Number(args.quoteLseIdRaw);
        const lseMatches = Number.isFinite(quoteLseId)
            ? products.filter((ele: any) => lightreachProductLseId(ele) === quoteLseId)
            : products;
        if (Number.isFinite(quoteLseId) && lseMatches.length === 0) {
            return {
                eligibleLightreachProductId: '',
                lightreachTotalSaleAmount: 0,
                inEligibleLightreachQuote: true,
                priceValidationMessage: LIGHTREACH_PRICE_MSG,
            };
        }
        const contractType = resolveLightreachContractType(args.financeMethod, args.gridMethord);
        const pricingValue = Number(args.priceRaw);
        const escalator = normalizeLightreachEscalatorRate(args.escalatorRaw);
        const totalAdders = Number(args.totalAdders) || 0;

        const productsForContract = lseMatches.filter(
            (p: any) =>
                lrContractTypesEqual(p?.Type, contractType) && lrEscalationRatesEqual(p?.EscalationRate, escalator),
        );
        let targetGrossPrice = 0;
        if (productsForContract.length > 0) {
            const currentProduct = productsForContract.find((p: any) => lrKwhRatesEqual(p?.KwhRate, pricingValue));
            const baseEpc = Number(currentProduct?.TotalEpcPayment || 0);
            targetGrossPrice = baseEpc + totalAdders;
        }

        const rateMatches = productsForContract.filter(
            (product: any) =>
                lrContractTypesEqual(product?.Type, contractType) &&
                lrKwhRatesEqual(product?.KwhRate, pricingValue) &&
                lrEscalationRatesEqual(product?.EscalationRate, escalator),
        );
        const preferredId = String(args.preferredProductId ?? '').trim();
        const matchedProduct =
            (preferredId ? rateMatches.find((p: any) => String(p?.ProductId ?? '') === preferredId) : null) ||
            (rateMatches.length > 1 && totalAdders > 0
                ? rateMatches.find((p: any) => Number(p?.TotalEpcPayment) >= targetGrossPrice) ||
                  rateMatches[rateMatches.length - 1]
                : rateMatches[0]) ||
            lseMatches.find(
                (product: any) =>
                    lrContractTypesEqual(product?.Type, contractType) &&
                    lrKwhRatesEqual(product?.KwhRate, pricingValue) &&
                    lrEscalationRatesEqual(product?.EscalationRate, escalator),
            );

        if (matchedProduct) {
            const totalEpc = Number(matchedProduct?.TotalEpcPayment) || 0;
            return {
                eligibleLightreachProductId: String(matchedProduct?.ProductId ?? '').trim(),
                lightreachTotalSaleAmount: totalEpc - totalAdders,
                inEligibleLightreachQuote: false,
                priceValidationMessage: '',
            };
        }

        return {
            eligibleLightreachProductId: '',
            lightreachTotalSaleAmount: 0,
            inEligibleLightreachQuote: true,
            priceValidationMessage: LIGHTREACH_PRICE_MSG,
        };
    }

    const getLightreachDerived = (currentFields?: FieldObject[]) => {
        const f = (name: string) => getFieldDetails(name, currentFields);
        const { priceRaw, escalatorRaw } = {
            priceRaw: f('Price')?.value && Number(f('Price')?.value) != 0 ?f('Price')?.value : f('Price')?.range?.targetRate ?? 0,
            escalatorRaw: f('Escalator')?.value,
        };
        const totalAdders = Number(f('AdderCost')?.value ?? 0) || 0;
        return deriveLightreachOfferAndTotal({
            isLightreach: isLightreachPartner,
            products: lightreachEligibleProductData,
            eligibleProductsReady: lightreachEligibleProductsReady,
            quoteLseIdRaw: resolveQuoteLseId(priceQuote),
            financeMethod: financeMethod || '',
            gridMethord: confirmQuoteCellKey?.methord || '',
            priceRaw,
            escalatorRaw,
            totalAdders,
            preferredProductId: Offer_Id,
        });
    };

    const quoteLseIdForLightreach = resolveQuoteLseId(priceQuote);

    const lightreachDerived = useMemo(
        () => getLightreachDerived(solarQuoteFormFields),
        [
            isLightreachPartner,
            lightreachEligibleProductData,
            lightreachEligibleProductsReady,
            quoteLseIdForLightreach,
            financeMethod,
            confirmQuoteCellKey?.methord,
            solarQuoteFormFields,
            Offer_Id,
        ],
    );

    useEffect(() => {
        updateSolarObject(false, solarQuoteFormFields)
    },[lightreachDerived])

    useEffect(() => {
        if (!isLightreachPartner) return;
        const nextMessage = lightreachDerived.priceValidationMessage ?? '';
        setsolarQuoteFormFields((prev) => {
            const priceField = prev.find((e) => e.name === 'Price');
            if (!priceField || (priceField.message ?? '') === nextMessage) return prev;
            return prev.map((el) => (el.name === 'Price' ? { ...el, message: nextMessage } : el));
        });
    }, [isLightreachPartner, lightreachDerived.priceValidationMessage]);

    useEffect(() => {
        if (
            isLightreachPartner &&
            lightreachDerived.eligibleLightreachProductId &&
            lightreachDerived.lightreachTotalSaleAmount > 0
        ) {
            updateErrorMessage('');
        }
    }, [
        isLightreachPartner,
        lightreachDerived.eligibleLightreachProductId,
        lightreachDerived.lightreachTotalSaleAmount,
        updateErrorMessage,
    ]);

    /** Mirrors Angular's `enfinTotalSaleAmount` / `enfinMatchedPricingOffer` instance fields, which survive
     *  across recalculations until `validateEnfinQuote` actually reassigns them. */
    const enfinRetainedTotalsRef = useRef<EnfinRetainedTotals>({
        enfinTotalSaleAmount: 0,
        enfinGrossSalesPrice: 0,
        enfinSolarSalesPrice: 0,
        enfinMatchedPricingOffer: null,
    });

    const getEnfinDerived = (currentFields?: FieldObject[]) => {
        const pricingMethodValue = getFieldDetails('PricingMethod', currentFields)?.value;
        const selectedPricing = pricingMethodArr.find((a) => a.Value == pricingMethodValue);
        return deriveEnfinValidation({
            isEnfin: isEnfinPartner,
            enfinPricingLoaded,
            enfinPricingOfferDetails,
            enfinBatteryPricingOfferDetails,
            enfinBatteryPricingLoaded,
            pricingMethodText: selectedPricing?.Text || '',
            priceRaw: getFieldDetails('Price', currentFields)?.value,
            productTypeCode: confirmQuoteCellKey?.productType || 'S',
            batteryTotalAmount,
            retainedTotals: enfinRetainedTotalsRef.current,
        });
    };

    const enfinDerived = useMemo(
        () => getEnfinDerived(solarQuoteFormFields),
        [
            isEnfinPartner,
            enfinPricingLoaded,
            enfinPricingOfferDetails,
            enfinBatteryPricingOfferDetails,
            enfinBatteryPricingLoaded,
            pricingMethodArr,
            solarQuoteFormFields,
            batteryTotalAmount,
            confirmQuoteCellKey?.product,
            financeMethod,
            lineMode,
        ],
    );

    useEffect(() => {
        if (!isEnfinPartner) return;
        const nextMessage = enfinDerived.priceValidationMessage ?? '';
        setsolarQuoteFormFields((prev) => {
            const priceField = prev.find((e) => e.name === 'Price');
            if (!priceField) return prev;
            const hasRangeMessage =
                !!priceField.message &&
                (priceField.message.includes('should not be less than') ||
                    priceField.message.includes('should not be greater than'));
            if (hasRangeMessage) return prev;
            if ((priceField.message ?? '') === nextMessage) return prev;
            return prev.map((el) => (el.name === 'Price' ? { ...el, message: nextMessage } : el));
        });
    }, [isEnfinPartner, enfinDerived.priceValidationMessage]);

    useEffect(() => {
        if (!isEnfinPartner || !enfinPricingLoaded) return;
        enfinRetainedTotalsRef.current = {
            enfinTotalSaleAmount: enfinDerived.enfinTotalSaleAmount,
            enfinGrossSalesPrice: enfinDerived.enfinGrossSalesPrice,
            enfinSolarSalesPrice: enfinDerived.enfinSolarSalesPrice,
            enfinMatchedPricingOffer: enfinDerived.enfinMatchedPricingOffer,
        };
        updateSolarObject(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync valid flag + totals when Enfin validation changes
    }, [
        isEnfinPartner,
        enfinPricingLoaded,
        enfinDerived.inEligibleEnfinQuote,
        enfinDerived.noEligibleEnfinProducts,
        enfinDerived.noEligibleEnfinSolarBatteryProducts,
        enfinDerived.enfinGrossSalesPriceExceedsRange,
        enfinDerived.enfinTotalSaleAmount,
        enfinDerived.enfinGrossSalesPrice,
        enfinDerived.formErrorMessage,
    ]);

    const solarFieldValidationSignature = useMemo(
        () =>
            solarQuoteFormFields
                .filter((field) => String(field.message ?? '').trim())
                .map((field) => `${field.name}:${field.message}`)
                .join('|'),
        [solarQuoteFormFields],
    );

    useEffect(() => {
        updateSolarObject(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh footer validation when inline field messages change
    }, [solarFieldValidationSignature, goodLeapValidationErrorMessage]);

    useEffect(() => {
        if (!isEnfinPartner || !enfinPricingLoaded) return;
        updateSolarObject(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- revalidate SB gross cap when battery line total changes
    }, [batteryTotalAmount, isEnfinPartner, enfinPricingLoaded]);

    const normalizePartnerToken = (value: any): string =>
        String(value ?? '')
            .replace(/\s+/g, '')
            .trim()
            .toLowerCase();

    const getDefaultPricingValue = (returnMinPricingValue: boolean = false, currentFields?: FieldObject[]) => {
        if (financeMethod === 'Cash Contract') {
            return 0;
        }
        let selectedPricingValue = pricingMethodArr.find(
            (a) => a.Value == getFieldDetails('PricingMethod', currentFields)?.value,
        );
        const systemSizeValue = priceQuote?.EPCCalc?.SystemSizeValue || 0;

        if (selectedPricingValue?.PricingValue == 'Dealer EPC Per Watt' || selectedPricingValue?.Text == 'Desired PPW (Net)') {
            return 0;
        } else if (selectedPricingValue?.PricingValue == 'EPC to Dealer' || selectedPricingValue?.Text == 'EPC') {
            return returnMinPricingValue === true ? (systemSizeValue * 1000 * 2.5) : 0;
        }
        return 0;
    }

    /**
     * Pure synchronous field update for Excel init (`updateOtherFields`). Clones `fields`, applies the same
     * rules as `setSolarQuoteFromValues`, and returns the new array without touching React state.
     */
    const updateSolarQuoteFormFieldsTemp = (
        fields: FieldObject[],
        field: string,
        value: any,
        resetPriceOnPricingMethodChange: boolean = false,
    ): FieldObject[] => {
        const solarQuoteFormFieldTemp = cloneFormFields(fields);
        solarQuoteFormFieldTemp.forEach((ele) => {
            if (ele.name == field) {
                ele.value = value;
            }
            if (field === 'Price' && ele.name === 'Price' && ele.type === 'Slider' && ele.range) {
                ele.range.nzValue = Number(value);
            }
            if (field === 'DesiredPPWNet' && ele.name === 'Price' && ele.type !== 'Slider') {
                ele.value = value;
            }
            if (field == 'PricingMethod' && ele.name == 'Price') {
                const pmOptions = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.options || [];
                const pmVal = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.value;
                const PricingMethodValue: string =
                    pmOptions.find((o) => o.Value == pmVal)?.Text || 'Pricing Value';
                ele.label = PricingMethodValue;
                // Angular `updateValues` keeps the current Price for LightReach; `getRateMinMaxForLightreach`
                // re-derives it from the new rate range instead, and needs the previous rate to match against.
                if (resetPriceOnPricingMethodChange && !isLightreachPartner) {
                    ele.value = '0';
                }
            }
            if (
                field == 'Escalator' &&
                ele.name == 'Price' &&
                resetPriceOnPricingMethodChange &&
                !isLightreachPartner
            ) {
                ele.value = '0';
                if (ele.type === 'Slider' && ele.range) {
                    ele.range.nzValue = 0;
                }
            }
            if (ele.name == 'Price' && (ele.enable || ele.haveAccess)) {
                const selectedPricingValue = pricingMethodArr.find(
                    (p) => p.Value == getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.value,
                );
                const priceVal = ele.value;
                if (
                    (selectedPricingValue?.PricingValue == 'EPC to Dealer' || selectedPricingValue?.Text == 'EPC') &&
                    Number(priceVal) < getDefaultPricingValue(true, solarQuoteFormFieldTemp)
                ) {
                    ele.message =
                        'The entered value should not be less than $' +
                        getDefaultPricingValue(true, solarQuoteFormFieldTemp);
                } else if (
                    (selectedPricingValue?.PricingValue == 'Dealer EPC Per Watt' ||
                        selectedPricingValue?.Text == 'Desired PPW (Net)') &&
                    Number(priceVal) < 2.5
                ) {
                    ele.message = 'The entered value should not be less than $2.5';
                } else if (ele.type === 'Slider') {
                    if ((ele.range?.nzMin != undefined || ele.range?.nzMin != null) && Number(priceVal) < ele.range?.nzMin) {
                        if (isEnfinPartner) {
                            ele.message =
                                'Invalid entry. Please enter a valid value. The selected pricing is not available from Enfin. Please click the "Enfin Eligible Product" button below to review all valid pricing available from Enfin.';
                        } else ele.message = `The entered value should not be less than ${ele.range?.nzMin}`;
                    } else if ((ele.range?.nzMax != undefined || ele.range?.nzMax != null) && Number(priceVal) > ele.range?.nzMax) {
                        if (isEnfinPartner) {
                            if (!priceVal || ele.range?.nzMax == 0) ele.message = 'Invalid entry. Please enter a valid value.';
                            else
                                ele.message =
                                    'Invalid entry. Please enter a valid value. The selected pricing is not available from Enfin. Please click the "Enfin Eligible Product" button below to review all valid pricing available from Enfin.';
                        } else ele.message = `The entered value should not be greater than ${ele.range?.nzMax}`;
                    } else {
                        ele.message = '';
                    }
                } else {
                    ele.message = '';
                }
            }

            if (ele.name == 'DesiredPPWNet' && (ele.enable || ele.haveAccess)) {
                const desiredFromTemp = Number(getFieldDetails(ele.name, solarQuoteFormFieldTemp)?.value ?? 0);
                if (desiredFromTemp < 2.5) {
                    ele.message = 'The entered value should not be less than $2.5';
                } else {
                    ele.message = '';
                }
            }

            if (ele.name == 'Deposit_Amount' && confirmQuoteCellKey?.partner == 'Cash' && (ele.enable || ele.haveAccess)) {
                const depositFromTemp = Number(getFieldDetails(ele.name, solarQuoteFormFieldTemp)?.value ?? 0);
                if (totalSaleAmount != 0 && depositFromTemp > totalSaleAmount) {
                    ele.message = `The entered Deposit Amount cannot be greater than $ ${Number(totalSaleAmount).toFixed(2)}`;
                } else {
                    ele.message = '';
                }
            }

            if (
                (ele.name === 'RebateValue' || ele.name === 'RebateAmountPerWatt') &&
                (ele.enable || ele.haveAccess)
            ) {
                const rebateMethod = getFieldDetails('RebateMethod', solarQuoteFormFieldTemp)?.value;
                const rebateVal = Number(
                    ele.name === 'RebateAmountPerWatt'
                        ? getFieldDetails('RebateAmountPerWatt', solarQuoteFormFieldTemp)?.value ?? 0
                        : getFieldDetails('RebateValue', solarQuoteFormFieldTemp)?.value ?? 0,
                );
                const desiredPpw = Number(getFieldDetails('DesiredPPWNet', solarQuoteFormFieldTemp)?.value ?? 0);
                ele.message = getRebatePerWattValidationMessage(rebateMethod, rebateVal, desiredPpw);
            }

            if (field === 'RebateMethod') {
                if (ele.name === 'RebateValue' || ele.name === 'RebateAmountPerWatt') {
                    ele.value = '0';
                    ele.message = '';
                }
            }

            if (
                field === 'RebateValue' &&
                ele.name === 'RebateAmountPerWatt' &&
                getFieldDetails('RebateMethod', solarQuoteFormFieldTemp)?.value === 'Per Watt'
            ) {
                ele.value = value;
            }
            if (
                field === 'RebateAmountPerWatt' &&
                ele.name === 'RebateValue' &&
                getFieldDetails('RebateMethod', solarQuoteFormFieldTemp)?.value === 'Per Watt'
            ) {
                ele.value = value;
            }
        });
        if (field === 'PricingMethod' || field === 'Escalator') {
            const escolator = getFieldDetails('Escalator', fields)?.value;
            applyPriceFieldFromPricingMethod(
                solarQuoteFormFieldTemp,
                financialArr,
                !isLightreachPartner &&
                    ((resetPriceOnPricingMethodChange && field === 'Escalator') || (field === 'PricingMethod' && escolator == '')),
            );
        }
        return solarQuoteFormFieldTemp;
    };

    /**
     * Single entry point for UI field updates (dropdowns always; amount fields on blur unless `priventDefault` is true).
     * Mutates a temp array, commits with `setsolarQuoteFormFields`, then runs partner-specific follow-up:
     * GoodLeap → API/total pipeline; Sunlight → dedicated handler; others → Excel total (`updateTotalSaleAmount`) + parent sync (`updateSolarObject`).
     * Use `getFieldDetails(..., solarQuoteFormFieldTemp)` below so validation sees the value just written (state is async).
     *
     * `resetPriceOnPricingMethodChange`: pass true only from the Pricing Method or Escalator dropdown `onChange`.
     * API/init code calls `setSolarQuoteFromValues('PricingMethod' | 'Escalator', …)` without it so Price is not cleared on load.
     */
    const setSolarQuoteFromValues = (
        field: string,
        value: any,
        priventDefault: boolean = false,
        resetPriceOnPricingMethodChange: boolean = false
    ) => {
        setsolarQuoteFormFields((prev) => { 
            const baseFields = initFormFieldsRef.current ?? prev;
            const solarQuoteFormFieldTemp = updateSolarQuoteFormFieldsTemp(
                baseFields,
                field,
                value,
                resetPriceOnPricingMethodChange,
            );
            if (initFormFieldsRef.current) {
                initFormFieldsRef.current = solarQuoteFormFieldTemp;
            }
            
            // `priventDefault` is set for amount `onChange` so we only recalc on `onBlur` (avoids Excel/API spam per keystroke).
            if (!priventDefault) {
                const changedField = getFieldDetails(field, solarQuoteFormFieldTemp);
                const rebateFields = ['RebateValue', 'RebateAmountPerWatt'];
                const fieldHasError =
                    !!changedField?.message ||
                    (rebateFields.includes(field) &&
                        !!solarQuoteFormFieldTemp.find(
                            (f) => rebateFields.includes(f.name) && (f.enable || f.haveAccess) && f.message,
                        ));
                if (!fieldHasError) {
                    if (isGoodLeap) {
    
                        if (confirmQuoteCellKey?.payload?.offerId) {
                            let offerId = ''
                            if (existingOfferIdDetails && existingOfferIdDetails.FinanceMethod == financeMethod
                                && ((financeMethod.includes('Loan') && getFieldDetails('TermAPR')?.value?.trim() == existingOfferIdDetails.TermAPR.trim())
                                    || (!financeMethod.includes('Loan') && existingOfferIdDetails.Escalator == getFieldDetails('Escalator')?.value
                                        && !existingOfferIdDetails.isUtilityUpdated))) {
                                offerId = existingOfferIdDetails.OfferId;
                                if (confirmQuoteCellKey) confirmQuoteCellKey.payload.offerId = existingOfferIdDetails.OfferId;
                                setOffer_Id(existingOfferIdDetails.OfferId)
                            } else {
                                setOffer_Id(confirmQuoteCellKey?.payload?.offerId);
                                offerId = confirmQuoteCellKey?.payload?.offerId;
                            }
                            updateGoodLeapTotalSaleAmount(false, offerId, solarQuoteFormFieldTemp);
                        } else {
                            goodLeapDropDownChange();
                        }
    
                    } else if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunlight') {
                        getSunlightPartnerTotalSaleAmount(solarQuoteFormFieldTemp);
                    } else if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'lightreach') {
                        updateSolarObject(false, solarQuoteFormFieldTemp);
                    }  else if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'igs') {
                        const leadId = opportunityDetails?.Finance_Partner_Sync_LeadID
                            ?? priceQuote?.Finance_Partner_Sync_LeadID;
                        if (!leadId){
                            updateErrorMessage("Lead ID not found, please save the lead first");
                            return solarQuoteFormFieldTemp;
                        } else {
                            updateTotalSaleAmount(false, solarQuoteFormFieldTemp);
                        }
                    } else if (enfinInitLoadRef.current && isEnfinPartner) {
                        updateSolarObject(false, solarQuoteFormFieldTemp);
                    } else {
                        // Default path: refresh totals from Solar Calculator sheet; parent sync runs in updateTotalSaleAmount `.then`.
                        updateTotalSaleAmount(false, solarQuoteFormFieldTemp);
                    }
                }
            }
            return solarQuoteFormFieldTemp;
        });
    }

    const getTermAndAPR = (data: string) => {
        return data.replace(' w/ ACH', '').replace('w/o ACH', '').trim();
    }

    const getExcelFinancePartner = (partner: string) => {
        switch (partner) {
            case 'GoodLeap':
            case 'Goodleap':
                partner = 'Good Leap';
        }
        return partner;
    }

    const getSvaC4 = (methodOverride?: string) => {
        if (lineMode === 'battery') {
            return batterySvaC4(
                confirmQuoteCellKey?.partner || '',
                confirmQuoteCellKey?.product,
                methodOverride ?? financeMethod ?? (confirmQuoteCellKey?.methord as string) ?? '',
            );
        }
        return svaProductCodeFromColumn(confirmQuoteCellKey?.product);
    };

    /** Calls Excel "Solar Calculator" / "Solar IO" via cascadingDropDown; writes results to state and `salesAmoontDetailsRef`. Async — callers that need fresh totals should use the `.then` path or ref after completion. */
    const updateTotalSaleAmount = (flag: boolean = false, currentFields?: FieldObject[]) => {
        if (lineMode === 'battery') return;
        if (updateTotalSaleAmountInFlightRef.current) {
            return;
        }
        updateTotalSaleAmountInFlightRef.current = true;

        const fieldsForRequest = cloneFormFields(
            currentFields ?? initFormFieldsRef.current ?? solarQuoteFormFields,
        );
        const fieldAt = (name: string) => getFieldDetails(name, fieldsForRequest);

        let selectedPricingValue = null
        if(fieldAt('PricingMethod')?.value != null && fieldAt('PricingMethod')?.value != '' && fieldAt('PricingMethod')?.value != undefined)
            selectedPricingValue = pricingMethodArr.find(a => a.Value == fieldAt('PricingMethod')?.value || a.Text == fieldAt('PricingMethod')?.value);
        
        let payload = {
            "ExcelFileName": "Solar Calculator",
            "SheetName": "Solar IO",
            "InputValues": [
                {
                    "Type": "String",
                    "CellNumber": "C3",
                    "CellValue": priceQuote.InstallationState
                },
                {
                    "Type": "String",
                    "CellNumber": "C4",
                    "CellValue": priceQuote.EPCCalc.SystemSizeValue
                },
                {
                    "Type": "String",
                    "CellNumber": "C5",
                    "CellValue": getSvaC4()
                },
                {
                    "Type": "String",
                    "CellNumber": "C6",
                    "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C7",
                    "CellValue": confirmQuoteCellKey?.methord || ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C8",
                    "CellValue": (fieldAt('TermAPR')?.value) ? getTermAndAPR(fieldAt('TermAPR')?.value.trim()) : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C9",
                    "CellValue": (selectedPricingValue?.Text == 'Desired PPW (Net)' || selectedPricingValue?.Text == 'EPC' 
                        || confirmQuoteCellKey?.partner == 'Cash') ?
                        ((!fieldAt('Price')?.value || fieldAt('Price')?.value == 0) ? fieldAt('DesiredPPWNet')?.value : fieldAt('Price')?.value) : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C10",
                    "CellValue": (fieldAt('Deposit_Amount')?.value) ? fieldAt('Deposit_Amount')?.value : fieldAt('DownPayment')?.value
                },
                {
                    "Type": "String",
                    "CellNumber": "C11",
                    "CellValue": fieldAt('RebateMethod')?.value === "Per Watt"
                        ? getRebateEntryValue(fieldsForRequest)
                        : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C12",
                    "CellValue": fieldAt('Tax')?.value || 0
                },
                {
                    "Type": "String",
                    "CellNumber": "C13",
                    "CellValue": (confirmQuoteCellKey?.partner == 'Cash' ? fieldAt('AdderPrice')?.value : fieldAt('AdderCost')?.value || 0)
                },
                {
                    "Type": "String",
                    "CellNumber": "C14",
                    "CellValue": selectedPricingValue?.Text
                },
                {
                    "Type": "String",
                    "CellNumber": "C15",
                    "CellValue": priceQuote.TSRF
                }
            ],
            "OutputCells": ["F6", "F7", "F5", "F3", "F9", "F4", "F10", "F8", "F12"]
        }
        if (financeMethod === 'Loan' && selectedPricingValue?.Text === 'EPC' && confirmQuoteCellKey?.partner == 'Sunlight') {
            setTotalSaleAmount(fieldAt('Price')?.value);
            setFinancedAmount((fieldAt('Deposit_Amount')?.value) ? totalSaleAmount - Number(fieldAt('Deposit_Amount')?.value) : totalSaleAmount)
        } else {
            setTotalSaleAmount(0);
            setFinancedAmount(0);
        }
        updateLoaderCount(true);
        salesAmoontDetailsRef.current = {};
        masterServices.cascadingDropDown(payload).then((data: ApiResponse) => {
            setRebateAmount(0);

            salesAmoontDetailsRef.current = {
                TotalSaleAmount: data[0].CellValue,
                FinancedAmount: data[1].CellValue,
                GrossSalesPricePerWatt: data[2].CellValue,
                DealerFeePercentage: data[3].CellValue,
                DealerEpc: data[1].CellValue,
                DealerTotalSaleAmount: data[4].CellValue,
                DealerFinancedAmount: data[5].CellValue,
                DealerGrossSalesPricePerWatt: data[6].CellValue,
                DealerDealerFeePercentage: data[7].CellValue,
                DealerDealerEpc: data[8].CellValue
            }
            setTotalSaleAmount(data[0].CellValue);
            if (selectedPricingValue?.Text == 'Desired PPW (Net)') {
                switch (getFieldDetails('RebateMethod')?.value) {
                    case 'Per Watt':
                        setRebateAmount(data.OutputValues[5].CellValue);
                        break;
                    case 'Fixed':
                        setRebateAmount(getFieldDetails('RebateValue')?.value);
                        break;
                    default:
                }
            } else {
                const systemSizeValue = priceQuote?.EPCCalc?.SystemSizeValue || 0;
                switch (getFieldDetails('RebateMethod')?.value) {
                    case 'Per Watt':
                        setRebateAmount(
                            systemSizeValue *
                                1000 *
                                Number(getRebateEntryValue(solarQuoteFormFields)),
                        );
                        break;
                    case 'Fixed':
                        setRebateAmount(getFieldDetails('RebateValue')?.value);
                        break;
                    default:
                }
            }
            let syncedFields = fieldsForRequest;
            setsolarQuoteFormFields((prev) => {
                syncedFields = syncPriceFieldsAfterExcelTotal(prev, selectedPricingValue);
                return syncedFields;
            });
            setGrossSalesPricePerWatt(data[2].CellValue);
            setDealerFeePercentage(data[3].CellValue);
            setDealerEpc(data[1].CellValue);
            setMonthly_Finance_Payment_NoITCPayback(data[4].CellValue); //F9
            setITCAmount(data[6].CellValue);
            setMonthly_Finance_Partner_Payment(data[7].CellValue); //F8
            if (!(financeMethod === 'Loan' && selectedPricingValue?.Text === 'EPC' && confirmQuoteCellKey?.partner == 'Sunlight')) {
                setFinancedAmount(Number(data[8].CellValue)); //F12
            }
            updateSolarObject(true, syncedFields);
            updateTotalSaleAmountInFlightRef.current = false;
            updateLoaderCount(false);
        }).catch(() => {
            updateTotalSaleAmountInFlightRef.current = false;
            updateLoaderCount(false);
        });
    }

    /** Parse display string from CurrencyInput / masked inputs (strip $, commas, etc.). */
    const parseAmountDisplayString = (display: string) => {
        const n = parseFloat(String(display).replace(/[^0-9.-]/g, ''));
        return Number.isFinite(n) ? n : 0;
    };

    /** Keeps Price slider + manual CurrencyInput within API-provided min/max (single value, no duplicate React state). */
    const clampPriceToSliderRange = (n: number, min?: number, max?: number) => {
        if (!Number.isFinite(n)) n = 0;
        const hasLo = min !== undefined && Number.isFinite(min);
        const hasHi = max !== undefined && Number.isFinite(max);
        if (!hasLo && !hasHi) return n;
        const lo = hasLo ? (min as number) : n;
        const hi = hasHi ? (max as number) : n;
        if (hi < lo) return n;
        return Math.min(Math.max(n, lo), hi);
    };

    const validateFieldValue = (event: React.KeyboardEvent<HTMLInputElement>, field: FieldObject) => {
        if (field.type !== 'amount' && !(field.name === 'Price' && field.type === 'Slider')) return;
        const isControlKey = event.key.length > 1;
        if (isControlKey) return;

        // CurrencyInput formats with "$" and grouping commas — a /\d*\.?\d*$/ regex on raw `value` always fails and blocked all keys.
        // Decimals and negatives are enforced by CurrencyInput props; optional live messages only below.
        const { value } = event.currentTarget;
        const numericGuess = parseAmountDisplayString(value);

        if (field.name === 'DesiredPPWNet') {
            const message =
                numericGuess > 0 && numericGuess < 2.5
                    ? 'The entered value should not be less than $2.5'
                    : null;
            setsolarQuoteFormFields((prev) =>
                prev.map((ele) => (ele.name === field.name ? { ...ele, message } : ele)),
            );
        }
        if (field.name === 'Deposit_Amount') {
            const message =
                totalSaleAmount && numericGuess > totalSaleAmount
                    ? `The entered Deposit Amount cannot be greater than $${Number(totalSaleAmount).toFixed(2)}`
                    : null;
            setsolarQuoteFormFields((prev) =>
                prev.map((ele) => (ele.name === field.name ? { ...ele, message } : ele)),
            );
        }
        if (field.name === 'RebateValue' || field.name === 'RebateAmountPerWatt') {
            const rebateMethod = getFieldDetails('RebateMethod')?.value;
            const desiredPpw = Number(getFieldDetails('DesiredPPWNet')?.value ?? 0);
            const message = getRebatePerWattValidationMessage(rebateMethod, numericGuess, desiredPpw) || null;
            setsolarQuoteFormFields((prev) =>
                prev.map((ele) =>
                    ele.name === 'RebateValue' || ele.name === 'RebateAmountPerWatt'
                        ? { ...ele, message }
                        : ele,
                ),
            );
        }
    };

    const getDownpaymentList = (financeType: any) => {
        if (!confirmQuoteCellKey?.clonedQuoteDetails) setSolarQuoteFromValues('DownPayment', 0)
        if (financeType != '') {
            if (confirmQuoteCellKey?.partner !== 'Sunnova') {
                if (confirmQuoteCellKey?.partner != 'IGS') {
                    solarQuoteFormFields.forEach((ele: any) => {
                        let fmOptions = solarFinanceMethods.filter((elem: any) => elem.Text == 'Prepaid PPA-EZ');
                        if (fmOptions.length > 0) {
                            if (ele.field == 'DownPayment' && fmOptions[0].Value == financeMethod) {
                                ele.enable = true;
                                ele.dataType = 'Amount';
                            }
                        } else {
                            if (ele.field == 'DownPayment') {
                                ele.enable = true;
                                ele.dataType = 'Amount';
                            }
                        }
                    })
                }
            }
        }
    }

    const applyFinancialFieldAccess = (
        financialTokens: string[],
        fields: FieldObject[],
        partnerName?: string,
    ): FieldObject[] => {
        const isCashPartner = (partnerName || '').trim().toLowerCase() === 'cash';
        const enabledNames = new Set(
            financialTokens
                .map((token) => resolveSolarFieldNameFromFinancialToken(token))
                .filter((name): name is string => !!name),
        );

        return fields.map((element) => {
            if (CASH_ONLY_FIELD_NAMES.includes(element.name)) {
                return { ...element, enable: isCashPartner };
            }
            if (enabledNames.has(element.name)) {
                return { ...element, enable: true };
            }
            return element;
        });
    };

    // const enableFields = (field: string, currentFields?: FieldObject[]) => {
    //     const base = currentFields ?? solarQuoteFormFields;
    //     const resolved = resolveSolarFieldNameFromFinancialToken(field);
    //     const solarQuoteFormFieldsTemp = base.map((element) => {
    //         if (
    //             resolved === element.name ||
    //             element.label === field.trim() ||
    //             element.name === field.trim()
    //         ) {
    //             return { ...element, enable: true };
    //         }
    //         return element;
    //     });
    //     if (currentFields) {
    //         return solarQuoteFormFieldsTemp;
    //     }
    //     setsolarQuoteFormFields(solarQuoteFormFieldsTemp);
    //     return solarQuoteFormFieldsTemp;
    // }

    const setEscolatorRange = (escalatorArr: any[]) => {
        let field = solarQuoteFormFields.find(element => element.name == 'Escalator');
        if (field?.enable) {
            let marks: any[] = []
            let escalatorArrTemp = [...escalatorArr];
            let nzMin = Number(escalatorArrTemp[0].Text);
            let nzMax = Number(escalatorArrTemp[0].Text);

            escalatorArrTemp.forEach(ele => {
                if (nzMin > Number(ele.Text)) nzMin = Number(ele.Text);
                else if (nzMax < Number(ele.Text)) nzMax = Number(ele.Text);
                marks[Number(ele.Text)] = '$' + String(ele.Text);
            });
            let rage: any = {
                nzMin: nzMin,
                nzMax: nzMax,
                nzMarks: marks,
                nzTooltipVisible: 'default'
            };
            setsolarQuoteFormFields((prev) => prev.map((ele: any) => (ele.name == field.name ? { ...ele, rage } : ele)));
        }
    }

    const retrieveSliderDetails = (
        field: any,
        currentFields?: FieldObject[],
        pricingMethodArr?: any[],
        skipAutoPriceUpdate: boolean = false,
        skipLoader: boolean = false,
    ): Promise<void> => {
        if (lineMode === 'battery') return Promise.resolve();
        let selectedPricingValue = pricingMethodArr?.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        const payload = {
            ExcelFileName: "Sunnova Financial Data",
            SheetName: "SVA Financing Options",
            InputValues: [
                { CellNumber: "B4", CellValue: priceQuote.InstallationState, Type: "String" },
                { CellNumber: "C4", CellValue: getSvaC4(), Type: "String" },
                { CellNumber: "D4", CellValue: (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '', Type: "String" },
                { CellNumber: "E8", CellValue: priceQuote.ExcelUtilityCompanyName, Type: "String" },
                { CellNumber: "F4", CellValue: confirmQuoteCellKey?.methord, Type: "String" },
                { CellNumber: "K4", CellValue: selectedPricingValue?.Text, Type: "String" },
                { CellNumber: "M4", CellValue: getFieldDetails('Escalator', currentFields)?.value, Type: "String" }
            ],
            OutputCells: ["K11"]
        };
        if (!skipLoader) updateLoaderCount(true);
        return masterServices.cascadingDropDown(payload).then((data: ApiResponse) => {
            if (data[0].CellValue) {
                const value = JSON.parse(data[0].CellValue);
                pricingDetailsFromExcelRef.current = value;
                const partnerLower = confirmQuoteCellKey?.partner?.toLocaleLowerCase();

                if (partnerLower === 'lightreach') {
                    getRateMinMaxForLightreach(field, currentFields, skipAutoPriceUpdate);
                    if (!skipLoader) updateLoaderCount(false);
                    return;
                }

                if (partnerLower === 'enfin') {
                    let solarQuoteFormFieldTemp = [...solarQuoteFormFields];
                    const outputValue = value.Output;
                    const escalatorValue = getFieldDetails('Escalator', currentFields)?.value;
                    const startValue = outputValue[`${escalatorValue} Rate Start`];
                    solarQuoteFormFieldTemp.forEach((ele) => {
                        if (ele.name == field.name) {
                            if(!confirmQuoteCellKey?.clonedQuoteDetails){
                                ele.value = skipAutoPriceUpdate ? '0' : startValue;
                            }
                        }
                    });
                    setsolarQuoteFormFields(solarQuoteFormFieldTemp);
                    if (!skipLoader) updateLoaderCount(false);
                    return startValue;
                }

                if (partnerLower !== 'goodleap' && !skipAutoPriceUpdate) {
                    let marks: any = {};
                    let red = 0, yellow = 0, green = 100;
                    if (confirmQuoteCellKey?.partner == "Sunnova") {
                        if (selectedPricingValue?.Text === 'PPW') {
                            marks[value['SunnovaPPWMin']] = '$' + String(value['SunnovaPPWMin']);
                            marks[value['SunnovaPPWMax']] = '$' + String(value['SunnovaPPWMax']);
                            const total = value['SunnovaPPWMax'] - value['SunnovaPPWMin'];
                            red = Math.round(((value.TrinityPPWFloor - value.SunnovaPPWMin) / total) * 100);
                            yellow = Math.round(((value.TrinityPPWMinimum - value.TrinityPPWFloor) / total) * 100);
                            green = Math.round(((value.SunnovaPPWMax - value.TrinityPPWMinimum) / total) * 100);
                        } else {
                            marks[value['SunnovaSolar RateMin']] = '$' + String(value['SunnovaSolar RateMin']);
                            marks[value['SunnovaSolar RateMax']] = '$' + String(value['SunnovaSolar RateMax']);
                        }
                        const style = `--red-area:${red}%;--yellow-area:${yellow}%;--red-yellow-area:${red + yellow}%;--green-area:${green}%;--slider-color:lightgreen`
                        field.range = {
                            nzMin: selectedPricingValue?.Text === 'PPW' ? value['SunnovaPPWMin'] : value['SunnovaSolar RateMin'],
                            nzMax: selectedPricingValue?.Text === 'PPW' ? value['SunnovaPPWMax'] : value['SunnovaSolar RateMax'],
                            nzValue: selectedPricingValue?.Text === 'PPW' ? value['SunnovaPPWStart'] : value['SunnovaSolar RateStart'],
                            nzStep: selectedPricingValue?.Text === 'PPW' ? 0.01 : .001,
                            nzMarks: marks,
                            nzTooltipVisible: 'default',
                            style: style
                        };
                    } else {
                        const outputValue = value.Output;
                        const escalatorValue = getFieldDetails('Escalator', currentFields)?.value;
                        const minValue = +outputValue[`${escalatorValue} Rate Min`];
                        const maxValue = +outputValue[`${escalatorValue} Rate Max`];
                        const startValue = +outputValue[`${escalatorValue} Rate Start`];
                        const splitPoint = ((startValue - minValue) / (maxValue - minValue)) * 100;
                        yellow = splitPoint;
                        green = 100 - yellow;
                        red = 0;
                        if (selectedPricingValue?.Text === 'PPW') {
                            marks[value.IGSRateMin] = '$' + String(value.IGSRateMin);
                            marks[value.IGSRateMax] = '$' + String(value.IGSRateMax);
                        } else {
                            marks[minValue] = '$' + String(minValue);
                            marks[maxValue] = '$' + String(maxValue);
                            marks[startValue] = {
                                style: {
                                    transform: 'translateX(-100%)',
                                    whiteSpace: 'nowrap'
                                },
                                label: '$' + startValue
                            };
                        }
                        const style = `--red-area:${red}%;--yellow-area:${yellow}%;--green-area:${green}%;--slider-color:linear-gradient(to right, yellow 0%, yellow ${yellow}%, lightgreen ${yellow}%, lightgreen 100%);--slider-track-color:transparent;--slider-segment-display:black`
                        field.range = {
                            nzMin: selectedPricingValue?.Text === 'PPW' ? value.IGSRateMin : minValue,
                            nzMax: selectedPricingValue?.Text === 'PPW' ? value.IGSRateMax : maxValue,
                            nzValue: selectedPricingValue?.Text === 'PPW' ? value.IGSRateStart : startValue,
                            nzStep: selectedPricingValue?.Text === 'PPW' ? 0.01 : .001,
                            nzMarks: marks,
                            nzTooltipVisible: 'default',
                            style: style
                        };
                    }
                    const requestedRate = Number(priceQuote?.RequestedPPARate);
                    let sliderValue = defaultPriceForPricingMethod(
                        selectedPricingValue?.Text,
                        confirmQuoteCellKey?.ExcelOffer,
                        field.range.nzValue,
                    );
                    if (
                        autoQuoteCreationInitiated &&
                        ((confirmQuoteCellKey?.partner == 'Sunnova' && financeMethod == 'PPA-EZ') ||
                            (confirmQuoteCellKey?.partner == 'IGS' && financeMethod == 'Lease')) &&
                        Number.isFinite(requestedRate) &&
                        requestedRate > 0
                    ) {
                        sliderValue = requestedRate;
                    }

                    let solarQuoteFormFieldTemp:any[] = currentFields ?? [... solarQuoteFormFields]
                    if (!confirmQuoteCellKey?.clonedQuoteDetails && !skipAutoPriceUpdate) {
                        setSolarQuoteFromValues('Price', sliderValue);
                    }
                    
                    field.enable = true; 
                    solarQuoteFormFieldTemp.forEach(ele => {
                        if (ele.name == field.name) {
                            const nextRange = { ...field.range };
                            if (!skipAutoPriceUpdate) {
                                nextRange.nzValue = sliderValue;
                                if (isSolarRatePricingMethod(selectedPricingValue?.Text)) {
                                    nextRange.targetRate =
                                        positiveOfferNumber(confirmQuoteCellKey?.ExcelOffer?.startingRate) ??
                                        positiveOfferNumber(confirmQuoteCellKey?.ExcelOffer?.StartingRate) ??
                                        nextRange.targetRate ??
                                        sliderValue;
                                }
                            }
                            ele.range = nextRange;
                            ele.value = skipAutoPriceUpdate ? '0' : sliderValue;
                        }
                    });
                    setsolarQuoteFormFields(solarQuoteFormFieldTemp);
                    updateSolarObject(false, currentFields);
                }
            }

            if (!skipLoader) updateLoaderCount(false);
        }).catch(() => {
            if (!skipLoader) updateLoaderCount(false);
        });
    }

    const getStartRateMark = (startValue: number, minValue: number, maxValue: number) => {
        const range = maxValue - minValue;
        let transform = 'translateX(-50%)';
        if (range > 0) {
            const position = ((startValue - minValue) / range) * 100;
            if (position <= 25) transform = 'translateX(0)';
            else if (position >= 75) transform = 'translateX(-100%)';
        }
        return {
            style: { transform, whiteSpace: 'nowrap', padding: '0 2px' },
            label: '$' + startValue,
        };
    };

    const findSolarRateForLightreach = (fieldDetails: FieldObject, currentFields?: FieldObject[]) => {
        const currentSolarPrice = Number(getFieldDetails(fieldDetails.name, currentFields)?.value);
        let matchValue = 0;
        const quoteLseId = resolveQuoteLseId(priceQuote);
        const solarProducts = lightreachSolarEligibleProducts(lightreachEligibleProductData);
        const lseMatches = Number.isFinite(quoteLseId)
            ? solarProducts.filter((ele: any) => lightreachProductLseId(ele) === quoteLseId)
            : solarProducts;
        if (lseMatches.length) {
            const escalator = normalizeLightreachEscalatorRate(getFieldDetails('Escalator', currentFields)?.value);
            const matchProducts = lseMatches
                .filter((product: any) => lrEscalationRatesEqual(product.EscalationRate, escalator))
                .map((item: any) => item.KwhRate);
            if (matchProducts.length && !matchProducts.includes(currentSolarPrice)) {
                matchValue = matchProducts.reduce((prev: number, curr: number) =>
                    Math.abs(curr - currentSolarPrice) < Math.abs(prev - currentSolarPrice) ? curr : prev,
                );
            } else {
                matchValue = currentSolarPrice;
            }
        }
        return matchValue;
    };

    const getSliderCssVars = (style?: string): React.CSSProperties => {
        if (!style) return {};
        return style.split(';').reduce((acc, part) => {
            const [key, ...valueParts] = part.split(':');
            const value = valueParts.join(':').trim();
            if (key?.trim() && value) {
                (acc as Record<string, string>)[key.trim()] = value;
            }
            return acc;
        }, {} as React.CSSProperties);
    };

    const isLightreachPpaMethod = (methodRaw: string): boolean => {
        const method = String(methodRaw ?? '').trim();
        return method === 'PPA' || method === 'PPA Storage';
    };

    const clampLightreachSolarRate = (rate: number, minPrice: number, maxPrice: number): number => {
        if (!Number.isFinite(rate)) return minPrice;
        if (maxPrice >= minPrice) return Math.min(Math.max(rate, minPrice), maxPrice);
        return rate;
    };

    /**
     * V1 Lightreach: `POST RateMinMax` → `MinPrice` / `MaxPrice` define the slider.
     * PPA initial adjustment = `ProposedRate` when present (clamped), otherwise `MinPrice` (e.g. 0.12).
     * Excel `${escalator} Rate Start` is the target mark / gradient split, not the initial field value.
     */
    const resolveLightreachInitialSolarRate = (minPrice: number, maxPrice: number): number => {
        const method = String(confirmQuoteCellKey?.methord ?? financeMethod ?? '').trim();
        const proposed = Number(priceQuote?.ProposedRate);
        if (isLightreachPpaMethod(method)) {
            if (Number.isFinite(proposed) && proposed > 0) {
                return clampLightreachSolarRate(proposed, minPrice, maxPrice);
            }
            return minPrice;
        }
        const excelOutput = pricingDetailsFromExcelRef.current?.Output;
        const escalator = getFieldDetails('Escalator')?.value;
        const startFromExcel =
            excelOutput && escalator != null && escalator !== ''
                ? +excelOutput[`${escalator} Rate Start`]
                : NaN;
        if (Number.isFinite(startFromExcel) && !Number.isNaN(startFromExcel)) {
            return clampLightreachSolarRate(startFromExcel, minPrice, maxPrice);
        }
        return minPrice;
    };

    const getRateMinMaxForLightreach = (
        field: FieldObject,
        currentFields?: FieldObject[],
        skipAutoPriceUpdate: boolean = false,
    ) => {
        const leadId =
            opportunityDetails?.Finance_Partner_Sync_LeadID ?? priceQuote?.Finance_Partner_Sync_LeadID;
        if (!leadId) {
            updateErrorMessage('Lead ID not found, please save the lead first');
            return;
        }
        const escalator = getFieldDetails('Escalator', currentFields)?.value;
        if (escalator === undefined || escalator === null || escalator === '') return;

        const lightreachValidation = deriveLightreachOfferAndTotal({
            isLightreach: true,
            products: lightreachEligibleProductData,
            eligibleProductsReady: lightreachEligibleProductsReady,
            quoteLseIdRaw: resolveQuoteLseId(priceQuote),
            financeMethod: financeMethod || '',
            gridMethord: confirmQuoteCellKey?.methord || '',
            priceRaw: getFieldDetails('Price', currentFields)?.value,
            escalatorRaw: escalator,
        });

        const payload = {
            solar_Rate: 0,
            escalationRate: String(escalator),
            financeMethod: String(financeMethod || confirmQuoteCellKey?.methord || '').trim(),
            stateCode: priceQuote.InstallationState,
            utilityCompany: priceQuote.UtilityCompanyName,
            leadId,
            opportunityId: String(priceQuote.Opportunity_Id),
        };
        updateLoaderCount(true);
        quoteServices
            .getRateMinMaxForLightreach(payload)
            .then((data: ApiResponse) => {
                if (data) {
                    const minPrice = Number(data.MinPrice);
                    const maxPrice = Number(data.MaxPrice);
                    const excelOutput = pricingDetailsFromExcelRef.current?.Output;
                    const startFromExcel = excelOutput ? +excelOutput[`${escalator} Rate Start`] : NaN;
                    const excelTarget =
                        Number.isFinite(startFromExcel) && !Number.isNaN(startFromExcel)
                            ? startFromExcel
                            : minPrice;
                    const initialRate = resolveLightreachInitialSolarRate(minPrice, maxPrice);
                    const method = String(confirmQuoteCellKey?.methord ?? financeMethod ?? '').trim();
                    const sliderTargetRate = isLightreachPpaMethod(method) ? excelTarget : initialRate;
                    const splitPoint =
                        maxPrice > minPrice
                            ? ((sliderTargetRate - minPrice) / (maxPrice - minPrice)) * 100
                            : 0;
                    const yellow = splitPoint;
                    const green = 100 - yellow;
                    const red = 0;
                    const marks: Record<string, any> = {};
                    marks[minPrice] = '$' + minPrice;
                    marks[maxPrice] = '$' + maxPrice;
                    if (sliderTargetRate <= maxPrice && sliderTargetRate >= minPrice) {
                        marks[sliderTargetRate] = getStartRateMark(sliderTargetRate, minPrice, maxPrice);
                    }
                    const style = `--red-area:${red}%;--yellow-area:${yellow}%;--green-area:${green}%;--slider-color:linear-gradient(to right, yellow 0%, yellow ${yellow}%, lightgreen ${yellow}%, lightgreen 100%);--slider-track-color:transparent;--slider-segment-display:none`;
                    field.range = {
                        nzMin: minPrice,
                        nzMax: maxPrice,
                        nzValue: initialRate,
                        nzStep: 0.005,
                        nzMarks: marks,
                        nzTooltipVisible: 'default',
                        targetRate: sliderTargetRate,
                        escalationRate: escalator,
                        style,
                    };
                    field.enable = true;
                    field.type = 'Slider';

                    const methodText = getFieldDetails('PricingMethod', currentFields)?.options?.find(
                        (option) => String(option.Value) == String(getFieldDetails('PricingMethod', currentFields)?.value),
                    )?.Text;
                    let priceToSet = defaultPriceForPricingMethod(
                        methodText,
                        confirmQuoteCellKey?.ExcelOffer,
                        initialRate,
                    );
                    priceToSet = clampLightreachSolarRate(priceToSet, minPrice, maxPrice);
                    if (
                        autoQuoteCreationInitiated &&
                        isLightreachPartner &&
                        Number(priceQuote?.RequestedPPARate) > 0
                    ) {
                        priceToSet = clampLightreachSolarRate(
                            Number(priceQuote.RequestedPPARate),
                            minPrice,
                            maxPrice,
                        );
                    } else if (
                        lightreachValidation.inEligibleLightreachQuote &&
                        isLightreachPpaMethod(method) &&
                        Number(priceQuote?.ProposedRate) > 0
                    ) {
                        const proposed = Number(priceQuote.ProposedRate);
                        const nearestEligibleRate = findSolarRateForLightreach(field, currentFields);
                        priceToSet =
                            proposed > maxPrice
                                ? maxPrice
                                : proposed < minPrice
                                  ? minPrice
                                  : nearestEligibleRate > 0
                                    ? nearestEligibleRate
                                    : proposed;
                    }

                    if (!confirmQuoteCellKey?.clonedQuoteDetails && !skipAutoPriceUpdate) {
                        field.value = String(priceToSet);
                        setSolarQuoteFromValues('Price', priceToSet, true);
                        const priceInWorkingCopy =
                            currentFields?.find((ele) => ele.name === 'Price') ?? field;
                        priceInWorkingCopy.value = String(priceToSet);
                    }

                    const fieldsForSolarUpdate = currentFields
                        ? currentFields.map((f) =>
                              f.name === 'Price' ? { ...f, value: String(priceToSet) } : f,
                          )
                        : undefined;

                    setsolarQuoteFormFields((prev) =>
                        prev.map((ele) =>
                            ele.name === field.name
                                ? {
                                      ...ele,
                                      range: field.range,
                                      enable: true,
                                      type: 'Slider',
                                      value: String(priceToSet),
                                  }
                                : ele,
                        ),
                    );
                    updateSolarObject(false, fieldsForSolarUpdate ?? currentFields);
                }
                updateLoaderCount(false);
            })
            .catch(() => {
                updateLoaderCount(false);
            });
    };

    const clearEnfinPricing = () => {
        setEnfinPricingLoaded(true);
        setEnfinPricingOfferDetails([]);
        setEnfinBatteryPricingOfferDetails([]);
        setEnfinBatteryPricingLoaded(false);
    };

    const updateEnfinSlider = (
        field: FieldObject,
        data: any,
        currentFields?: FieldObject[],
        skipAutoPriceUpdate: boolean = false,
    ) : FieldObject  => {
        const minPrice = Number(data?.MinPrice);
        const maxPrice = Number(data?.MaxPrice);
        const hasPriceRange = !Number.isNaN(minPrice) && !Number.isNaN(maxPrice);
        const marks: Record<string, string> = {};
        if (hasPriceRange) {
            marks[minPrice] = '$' + String(minPrice);
            marks[maxPrice] = '$' + String(maxPrice);
        }
        const style =
            '--red-area:0%;--yellow-area:0%;--red-yellow-area:0%;--green-area:100%;--slider-color:lightgreen';
        field.range = {
            nzMin: hasPriceRange ? minPrice : 0,
            nzMax: hasPriceRange ? maxPrice : 0,
            nzValue: hasPriceRange ? minPrice : 0,
            nzStep: 0.005,
            nzMarks: marks,
            nzTooltipVisible: 'default',
            style,
        };
        field.enable = true;
        field.type = 'Slider';
        const enfinPricingMethod = getFieldDetails('PricingMethod', currentFields)?.options?.find(
            (option) => String(option.Value) == String(getFieldDetails('PricingMethod', currentFields)?.value),
        )?.Text;
        let sliderValue = defaultPriceForPricingMethod(
            enfinPricingMethod,
            confirmQuoteCellKey?.ExcelOffer,
            field.range.nzValue,
        );
        if (autoQuoteCreationInitiated && isEnfinPartner) {
            const requestedRate = Number(priceQuote?.RequestedPPARate);
            if (Number.isFinite(requestedRate) && requestedRate > 0) sliderValue = requestedRate;
        }
        const currentPrice = Number(getFieldDetails('Price', currentFields)?.value);
        
        if ((!confirmQuoteCellKey?.clonedQuoteDetails || !skipAutoPriceUpdate) && !currentPrice) {
            field.value = sliderValue;
            if (field.range) field.range.nzValue = sliderValue;
        } else if(currentPrice && !confirmQuoteCellKey?.clonedQuoteDetails || !skipAutoPriceUpdate) {
            if (field.range) field.range.nzValue = currentPrice;
        }

        return field
    };

    const fetchEnfinPricingCaps = async (
        field: FieldObject,
        currentFields?: FieldObject[],
        pricingMethodOptions?: any[],
        skipAutoPriceUpdate: boolean = false,
        force = false,
    ): Promise<void> => {
        const opportunityId =
            priceQuote?.Opportunity_Id ??
            opportunityDetails?.Opportunity_Id ??
            opportunityDetails?.OpportunityId ??
            storeOpportunityId ??
            storeSystemDesign?.Opportunity_Id ??
            storeSystemDesign?.OpportunityId;
        const escalatorRate =
            String(
                getFieldDetails('Escalator', currentFields)?.value ??
                    confirmQuoteCellKey?.ExcelOffer?.escalator ??
                    getEnfinEscalatorRateFromPriceQuote(priceQuote),
            ).trim() || '0';
        if (opportunityId == null || String(opportunityId).trim() === '') {
            return;
        }

        const basePayload = {
            opportunityId,
            escalatorRate,
            term: '25',
        };
        const fetchBattery = confirmQuoteCellKey?.productType === 'SB';
        const skipLoader = quoteServices.hasEnfinPriceCapsCache(basePayload)
            && (!fetchBattery || quoteServices.hasEnfinPriceCapsCache({ ...basePayload, batteryMode: true }));

        enfinPricingFetchStartedRef.current = true;
        const loadGen = ++enfinPricingLoadGenRef.current;
        if (!skipLoader && !enfinLoaderIncrementedRef.current) {
            updateLoaderCount(true);
            enfinLoaderIncrementedRef.current = true;
        }

        try {
            if (fetchBattery) {
                setEnfinBatteryPricingLoaded(false);
            }

            const solarRes = await quoteServices.getEnfinPriceCaps(basePayload, { force });
            const solarOffers = enfinOfferDetails(solarRes);
            const resolvedSolar =
                solarOffers.length > 0
                    ? solarRes
                    : await quoteServices.getEnfinPriceCaps(basePayload, { force: true });
            if (loadGen !== enfinPricingLoadGenRef.current) return;

            if (resolvedSolar) {
                const priceField = currentFields?.find((element) => element.name === 'Price') || field;
                const peiceField = updateEnfinSlider(priceField, resolvedSolar, currentFields, skipAutoPriceUpdate);
                currentFields?.forEach((ele:any) =>{
                    if(ele.name == 'Price') ele = peiceField
                })
                setEnfinPricingOfferDetails(enfinOfferDetails(resolvedSolar));
                setEnfinPricingLoaded(true);
                if (currentFields){
                    currentFields = updateSolarQuoteFormFieldsTemp(currentFields,'Price', peiceField.value)
                    setsolarQuoteFormFields([...currentFields]);
                } 
                updateSolarObject(false, currentFields);
            } else {
                clearEnfinPricing();
                if (currentFields) setsolarQuoteFormFields([...currentFields]);
                updateSolarObject(false, currentFields);
            }

            if (fetchBattery) {
                try {
                    const batteryPayload = { ...basePayload, batteryMode: true };
                    let batteryRes = await quoteServices.getEnfinPriceCaps(batteryPayload, { force });
                    if (!enfinOfferDetails(batteryRes).length) {
                        batteryRes = await quoteServices.getEnfinPriceCaps(batteryPayload, { force: true });
                    }
                    if (loadGen !== enfinPricingLoadGenRef.current) return;
                    setEnfinBatteryPricingLoaded(true);
                    setEnfinBatteryPricingOfferDetails(enfinOfferDetails(batteryRes));
                    updateSolarObject(false, currentFields);
                } catch {
                    if (loadGen !== enfinPricingLoadGenRef.current) return;
                    setEnfinBatteryPricingOfferDetails([]);
                    setEnfinBatteryPricingLoaded(true);
                    updateSolarObject(false, currentFields);
                }
            }
        } catch {
            if (loadGen !== enfinPricingLoadGenRef.current) return;
            clearEnfinPricing();
            if (currentFields) setsolarQuoteFormFields([...currentFields]);
            updateSolarObject(false, currentFields);
        } finally {
            if (loadGen === enfinPricingLoadGenRef.current) {
                enfinInitLoadRef.current = false;
                if (enfinLoaderIncrementedRef.current) {
                    updateLoaderCount(false);
                    enfinLoaderIncrementedRef.current = false;
                }
            }
        }
    };

    /**
     * Price as slider only for rate-based methods (PPW, Solar Rate, Rate). All other methods use a single CurrencyInput.
     * Called from `updateOtherFields` (Excel init) and from `setSolarQuoteFromValues` when the user changes Pricing Method
     * so `field.type` is not left as `Slider` when switching to non-rate methods (e.g. Monthly Payment on IGS).
     */
    const applyPriceFieldFromPricingMethod = (
        solarQuoteFormFieldTemp: FieldObject[],
        financialFieldsArr?: string[],
        skipAutoPriceUpdate: boolean = false,
    ) => {
        if (lineMode === 'battery') return;
        const pricingMethodOptions = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.options || [];
        const pmVal = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.value;
        const resolvedPricingMethod = pricingMethodOptions.find(
            (a: any) => String(a.Value) == String(pmVal),
        );
        const isRateBased = ['ppw', 'solar rate', 'rate'].includes(
            (resolvedPricingMethod?.Text ?? '').trim().toLowerCase(),
        );
        const element = solarQuoteFormFieldTemp.find((e) => e.name === 'Price');
        if (!element) return;

        if (!isGoodLeap && isRateBased) {
            element.type = 'Slider';
            if (normalizePartnerToken(confirmQuoteCellKey?.partner) === 'enfin') {
                if (financialFieldsArr && financialFieldsArr.length > 0) {
                    const data: any = financialFieldsArr?.find(
                        (el) =>
                            el.trim().toLowerCase() == element.label.trim().toLowerCase() ||
                            el.trim().toLowerCase() == element.name.trim().toLowerCase(),
                    );
                    element.enable = !!data;
                }
                const escalatorVal = getFieldDetails('Escalator', solarQuoteFormFieldTemp)?.value;
                if (escalatorVal != null && String(escalatorVal).trim() !== '') {
                    void fetchEnfinPricingCaps(
                        element,
                        solarQuoteFormFieldTemp,
                        pricingMethodOptions,
                        skipAutoPriceUpdate,
                    );
                    void retrieveSliderDetails(
                        element,
                        solarQuoteFormFieldTemp,
                        pricingMethodOptions,
                        skipAutoPriceUpdate,
                    );
                }
            } else if (confirmQuoteCellKey?.partner?.toLowerCase() === 'lightreach') {
                if (financialFieldsArr && financialFieldsArr.length > 0) {
                    const data: any = financialFieldsArr?.find(
                        (el) =>
                            el.trim().toLowerCase() == element.label.trim().toLowerCase() ||
                            el.trim().toLowerCase() == element.name.trim().toLowerCase(),
                    );
                    element.enable = !!data;
                }
                retrieveSliderDetails(element, solarQuoteFormFieldTemp, pricingMethodOptions, skipAutoPriceUpdate);
            } else {

                retrieveSliderDetails(element, solarQuoteFormFieldTemp, pricingMethodOptions, skipAutoPriceUpdate);
            }
        } else if (pmVal != null && pmVal !== '') {
            if (resolvedPricingMethod?.Text) {
                element.label = resolvedPricingMethod.Text;
            }
            if(financialFieldsArr && financialFieldsArr.length > 0) {
                const labelToMatch = (element.label || '').trim().toLowerCase();
                const nameToMatch = (element.name || '').trim().toLowerCase();
                const data: any = financialFieldsArr.find(
                    (el) => el.trim().toLowerCase() === labelToMatch || el.trim().toLowerCase() === nameToMatch,
                );
                element.enable = !!data;
            }
            if (
                (resolvedPricingMethod?.PricingValue == 'EPC to Dealer' || resolvedPricingMethod?.Text == 'EPC') &&
                (Number(element?.value) || 0) < getDefaultPricingValue(true, solarQuoteFormFieldTemp)
            ) {
                element.message =
                    'The entered value should not be less than $' + getDefaultPricingValue(true, solarQuoteFormFieldTemp);
            } else if (
                (resolvedPricingMethod?.PricingValue == 'Dealer EPC Per Watt' ||
                    resolvedPricingMethod?.Text == 'Desired PPW (Net)') &&
                    Number(element?.value) < 2.5  && element.enable
            ) {
                element.message = 'The entered value should not be less than $2.5';
            } else {
                element.message = '';
            }
            element.dataType = 'Amount';
            element.type = 'amount';
            element.range = undefined;
        }
        if (isEnfinPartner && !enfinPricingFetchStartedRef.current) {
            void fetchEnfinPricingCaps(
                element,
                solarQuoteFormFieldTemp,
                pricingMethodOptions,
                skipAutoPriceUpdate,
            );
        }
    };

    /**
     * Builds the payload for `updateSolarDetails` from current form fields, React state, and `salesAmoontDetailsRef`.
     * Partner-specific rules pick PPW, pricingValue, TotalSaleAmount, etc. (e.g. Sunnova loan + Desired PPW uses ref totals).
     * `flag` is forwarded from GoodLeap flows where the caller needs to signal offer/total refresh semantics.
     */
    const updateSolarObject = (flag: any, currentFields?: FieldObject[]) => {

        const fieldsForValidation = currentFields || solarQuoteFormFields;
        let selectedPricingValue = getFieldDetails('PricingMethod', currentFields)?.options?.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        const SyncProductInfo = priceQuote.SyncProductInfo.find((element: any) => {
            return element.Product_Type_Name == 'Solar' && element.FinancePartner == confirmQuoteCellKey?.partner && element.FinanceMethodName == financeMethod
        });

        const enfin = getEnfinDerived(currentFields);
        // Angular `updateSolarObject`: one `partnerTotalSaleAmount` feeds both `TotalSaleAmount` and `Total_Amount`.
        // Enfin reads the matched pricing offer total only — it never falls back to the Excel/Solar Calculator total.
        const solarAdderTotal = Number(getFieldDetails('AdderCost', currentFields)?.value ?? 0) || 0;
        const partnerTotalSaleAmount = isLightreachPartner
            ? Number(lightreachDerived.lightreachTotalSaleAmount) + solarAdderTotal
            : isEnfinPartner
                ? enfin.enfinTotalSaleAmount
                : (salesAmoontDetailsRef.current.TotalSaleAmount || totalSaleAmount);
        const lightreachFormInvalid = isLightreachPartner && lightreachDerived.inEligibleLightreachQuote;
        const enfinFormInvalid =
            isEnfinPartner &&
            (enfin.inEligibleEnfinQuote ||
                enfin.noEligibleEnfinProducts ||
                enfin.noEligibleEnfinSolarBatteryProducts ||
                enfin.enfinGrossSalesPriceExceedsRange);

        const fieldValidationError = haveValidationErrors(fieldsForValidation);
        const validationMessages = collectSolarValidationMessages(
            fieldsForValidation,
            enfin,
            lightreachFormInvalid,
            lightreachDerived.priceValidationMessage,
            enfinDerived.formErrorMessage,
        );
        const validationMessage = validationMessages.join(VALIDATION_MESSAGE_SEP);

        const data: any = {
            opportunity_id: priceQuote.Opportunity_Id,
            // financePartnerId: solarPartner?.Value,
            financePartnerName: confirmQuoteCellKey?.partner || '',
            contract_Type: confirmQuoteCellKey?.methord || '',
            // term: 0,
            // apr: 0,
            epc: Number(getFieldDetails('EPC', currentFields)?.value),
            rate: Number(getFieldDetails('Rate', currentFields)?.value),
            ppw: ((confirmQuoteCellKey?.partner?.replace(" ", "").toLocaleLowerCase() == 'goodleap' || confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunlight')
                && financeMethod === 'Loan' && (selectedPricingValue?.Text == 'Desired PPW (Net)' || selectedPricingValue?.Text == 'EPC')) ?
                Number(salesAmoontDetailsRef.current?.GrossSalesPricePerWatt || grossSalesPricePerWatt) : (salesAmoontDetailsRef.current.PPW || PPW) !== null ? Number(salesAmoontDetailsRef.current.PPW || PPW) : Number(getFieldDetails('PPW', currentFields)?.value),
            pricingmethodName: selectedPricingValue?.Text || '',
            pricingmethod: selectedPricingValue?.PricingValue || '',
            pricingValue: isEnfinPartner
                ? Number(enfin.enfinMatchedPricingOffer?.totalkwhrate || 0)
                : (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunnova'
                && SunnovaLoanOptions.indexOf(financeMethod.trim()) !== -1
                && selectedPricingValue?.Text == 'Desired PPW (Net)') ? (salesAmoontDetailsRef.current.TotalSaleAmount || totalSaleAmount) : Number(getFieldDetails('Price', currentFields)?.value),
            rebateMethod: getFieldDetails('RebateMethod', currentFields)?.value || '',
            rebate: Number(rebateAmount),
            Escalator: (getFieldDetails('Escalator', currentFields)?.value) ? getFieldDetails('Escalator', currentFields)?.value : 0,
            downPayment: Number(getFieldDetails('DownPayment', currentFields)?.value),
            tax: Number(getFieldDetails('Tax', currentFields)?.value),
            TermAndAPR: getFieldDetails('TermAPR', currentFields)?.value,
            design: getFieldDetails('DesignId', currentFields)?.value,
            PermitPayer: getFieldDetails('PermitPayer', currentFields)?.value,
            TotalSaleAmount: partnerTotalSaleAmount,
            DesiredPPWNet: (confirmQuoteCellKey?.partner?.replace(" ", "").toLocaleLowerCase() === 'goodleap' || confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunlight') ? getDesiredPPWNet(currentFields) : getFieldDetails('DesiredPPWNet', currentFields)?.value,
            RebateAmountPerWatt:
                getFieldDetails('RebateMethod', currentFields)?.value == 'Per Watt'
                    ? Number(getRebateEntryValue(currentFields || solarQuoteFormFields))
                    : 0,
            AdderPrice: getFieldDetails('AdderPrice', currentFields)?.value,
            AdderCost: getFieldDetails('AdderCost', currentFields)?.value,
            valid: (!!fieldValidationError || lightreachFormInvalid || enfinFormInvalid || (isEnfinPartner && enfinDerived.formErrorMessage)) ? false : true,
            validationMessage,
            grossSalesPricePerWatt: salesAmoontDetailsRef.current?.GrossSalesPricePerWatt || grossSalesPricePerWatt,
            dealerFeePercentage: salesAmoontDetailsRef.current?.DealerFeePercentage || dealerFeePercentage,
            dealerEpc: salesAmoontDetailsRef.current?.DealerEpc || dealerEpc,
            Monthly_Finance_Payment_NoITCPayback: salesAmoontDetailsRef.current?.Monthly_Finance_Payment_NoITCPayback,
            Monthly_Finance_Partner_Payment: salesAmoontDetailsRef.current?.Monthly_Finance_Partner_Payment,
            ITC_Amount: salesAmoontDetailsRef.current?.ITCAmount,
            // SyncProductInfo: (SyncProductInfo)? SyncProductInfo : this.SyncProductInfo,
            Finance_Partner_Product_Id: getFieldDetails('Finance_Partner_Product_Id', currentFields)?.value,
            Offer_Id: isLightreachPartner ? lightreachDerived.eligibleLightreachProductId : confirmQuoteCellKey?.payload?.offerId,
            IsACH: salesAmoontDetailsRef.current?.IsACH,
            Product_Type: ProductType,
            productionRate: isEnfinPartner
                ? Number(enfin.enfinMatchedPricingOffer?.solarkwhrate || 0)
                : (salesAmoontDetailsRef.current?.ProductionRate || productionRate),
            SolarProductionRate: isEnfinPartner
                ? Number(enfin.enfinMatchedPricingOffer?.solarkwhrate || 0)
                : (salesAmoontDetailsRef.current?.ProductionRate || productionRate),
            Deposit_Amount: getFieldDetails('Deposit_Amount', currentFields)?.value,
            Financed_Amount: salesAmoontDetailsRef.current.financedAmount || financedAmount,
            Total_Amount: partnerTotalSaleAmount,
            UponCommencementOfPhysicalInstall: getFieldDetails('UponCommencementOfPhysicalInstall', currentFields)?.value,
            UponCompletionOfPhysicalInstall: getFieldDetails('UponCompletionOfPhysicalInstall', currentFields)?.value,
            UponCompletionOfTheContract: getFieldDetails('UponCompletionOfTheContract', currentFields)?.value,
            cloneQuoteId: confirmQuoteCellKey?.clonedQuoteDetails?.QuoteId
        };
        updateSolarDetails(data);
    }

    /** Resolves Desired PPW (net) for GoodLeap/Sunlight when it is not a raw form field for the current loan/pricing mode. */
    const getDesiredPPWNet = (currentFields?: FieldObject[]): any => {
        const selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        const selectedPricingText = selectedPricingValue?.Text;

        if (financeMethod?.includes('Loan')) {
            if (selectedPricingText === 'Desired PPW (Net)') {
                return getFieldDetails('Price', currentFields)?.value;
            } else if (selectedPricingText === 'EPC') {
                return grossSalesPricePerWatt;
            }
        } else if (financeMethod === 'Lease' || financeMethod == 'PPA') {
            return PPW;
        }
        return getFieldDetails('DesiredPPWNet', currentFields)?.value
    }

    /** GoodLeap: derives purchase price / PPW from partner API when not on loan path; otherwise defers to `updateTotalSaleAmount`. */
    const updateGoodLeapTotalSaleAmount = (flag?: any, offerId?: any, currentFields?: FieldObject[]) => {
        setGoodLeapValidationErrorMessage('');
        const selectedPricingValue = (getFieldDetails("PricingMethod", currentFields)?.options || [])?.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        if (!priceQuote.ExcelUtilityCompanyName && isGoodLeap) setGoodLeapValidationErrorMessage('Utility information not saved');
        if (confirmQuoteCellKey?.methord === 'Loan' || confirmQuoteCellKey?.methord === 'Loan Storage') {
            updateTotalSaleAmount(flag, currentFields);
        } else {
            let purchasePrice = 0, kWhRate = 0, dollarPerWatt = 0, monthlyPayment = 0;
            const price = currentFields?.find(ele => ele.name == 'Price')?.value
            if (selectedPricingValue?.Text.trim() === 'Solar Rate') kWhRate = price;
            else if (selectedPricingValue?.Text.trim() === 'PPW') dollarPerWatt = price;
            else if (selectedPricingValue?.Text.trim() === 'Desired PPW (Net)') dollarPerWatt =  price;
            else if (selectedPricingValue?.Text.trim() == 'Monthly Payment') monthlyPayment =  price;
            else purchasePrice =  price;
            if (purchasePrice != 0 || kWhRate != 0 || dollarPerWatt != 0 || monthlyPayment != 0) {
                let payload = {
                    opportunityId: priceQuote.Opportunity_Id,
                    offerId: offerId,
                    systemSize: priceQuote.EPCCalc.SystemSizeValue,
                    estimatedAnnualProduction: ((priceQuote.EPCCalc.ProductionSizeValue ?? Number(priceQuote.EPCCalc.ProductionSizeValue))) * 1000,
                    kWhRate: kWhRate,
                    monthlyPayment: monthlyPayment,
                    dollarPerWatt: dollarPerWatt,
                    purchasePrice: purchasePrice
                }
                updateLoaderCount(true);
                updateErrorMessage('');
                quoteServices.updateGoodLeapTotalSaleAmount(payload).then((res: any) => {
                    salesAmoontDetailsRef.current = {
                        TotalSaleAmount: res.purchasePrice,
                        Monthly_Finance_Partner_Payment: res.firstYearMonthlyPayment,
                        PPW: res.PPW,
                        ProductionRate: res.productionRate,
                    }
                    setTotalSaleAmount(res.purchasePrice);
                    setMonthly_Finance_Partner_Payment(res.firstYearMonthlyPayment);
                    setPPW(res.PPW);
                    setGoodLeapValidationErrorMessage(res.description);
                    setProductionRate(res.productionRate);
                    updateLoaderCount(false);
                    updateSolarObject(flag, currentFields);
                    if (res.description){
                        let subMsg:string = ". Please refresh the design and try again";
                        if(res.description.includes("estimatedAnnualProduction")) subMsg = ". Please check utility details."
                        updateErrorMessage(res.description + subMsg);
                    }
                }).catch(() => {
                    updateLoaderCount(false);
                });
            } else {
                setTotalSaleAmount(0);
                updateSolarObject(false, currentFields);
            }
        }
    }

    const goodLeapDropDownChange = (flag?: any) => {
        const selectedPricingValue = (solarQuoteFormFields.find(a => a.name == 'PricingMethod')?.options || []).find(a => a.Value == getFieldDetails('PricingMethod')?.value);
        if (selectedPricingValue && selectedPricingValue.Text) {
            const FinancialData = {
                "ExcelFileName": "Sunnova Financial Data",
                "SheetName": "SVA Financing Options",
                "InputValues": [
                    { "Type": "String", "CellNumber": "B4", "CellValue": priceQuote.InstallationState },
                    { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4() },
                    { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
                    { "Type": "String", "CellNumber": "F4", "CellValue": confirmQuoteCellKey?.methord },
                    { "Type": "String", "CellNumber": "K4", "CellValue": selectedPricingValue?.Text },
                    { "Type": "String", "CellNumber": "M4", "CellValue": getFieldDetails('Escalator')?.value },
                    { "Type": "String", "CellNumber": "O4", "CellValue": getFieldDetails('TermAPR')?.value?.trim() },
                    { "Type": "String", "CellNumber": "E8", "CellValue": priceQuote.UtilityCompanyName || '' },
                ],
                "OutputCells": ["T4", "U11"],
            };
            updateLoaderCount(true);
            masterServices.cascadingDropDown(FinancialData).then((data: ApiResponse) => {
                let offerId = ''
                setFinance_Partner_Product_Id(data[1].CellValue);
                if (existingOfferIdDetails && existingOfferIdDetails.FinanceMethod == financeMethod
                    && ((financeMethod.includes('Loan') && getFieldDetails('TermAPR')?.value?.trim() == existingOfferIdDetails.TermAPR.trim())
                        || (!financeMethod.includes('Loan') && existingOfferIdDetails.Escalator == getFieldDetails('Escalator')?.value
                            && !existingOfferIdDetails.isUtilityUpdated))) {
                    offerId = existingOfferIdDetails.OfferId;
                    if(confirmQuoteCellKey) {
                        confirmQuoteCellKey.payload.offerId = existingOfferIdDetails.OfferId;
                    }
                    setOffer_Id(existingOfferIdDetails.OfferId)
                } else {
                    if(!!confirmQuoteCellKey) {
                        if(confirmQuoteCellKey.payload) confirmQuoteCellKey.payload.offerId = data[0].CellValue;
                        else confirmQuoteCellKey.payload = { offerId: data[0].CellValue };
                    }
                    setOffer_Id(data[0].CellValue);
                    offerId = data[0].CellValue;
                }
                updateLoaderCount(false);
                updateGoodLeapTotalSaleAmount(flag, offerId, solarQuoteFormFields);
            }).catch(() => {
                updateLoaderCount(false);
            });
        }
    }

    const getSunlightPartnerTotalSaleAmount = (currentFields?: FieldObject[]) => {
        const selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        if (financeMethod === 'Loan' && selectedPricingValue?.Text === 'EPC') {
            setTotalSaleAmount(getFieldDetails('Price', currentFields)?.value);
            setFinancedAmount((getFieldDetails('Deposit_Amount', currentFields)?.value) ? totalSaleAmount - Number(getFieldDetails('Deposit_Amount', currentFields)?.value) : totalSaleAmount)
            updateSolarObject(false);
        }
        if (selectedPricingValue?.Text === 'Desired PPW (Net)' || selectedPricingValue?.Text === 'EPC') {
            updateTotalSaleAmount(false, currentFields);
        }
    }

    const getSunlightPartnerValues = () => {
        const selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod')?.value);
        const financialData = {
            "ExcelFileName": "Sunnova Financial Data",
            "SheetName": "SVA Financing Options",
            "InputValues": [
                { "Type": "String", "CellNumber": "B4", "CellValue": priceQuote.InstallationState },
                { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4() },
                { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
                { "Type": "String", "CellNumber": "F4", "CellValue": financeMethod },
                { "Type": "String", "CellNumber": "K4", "CellValue": selectedPricingValue?.Text },
                { "Type": "String", "CellNumber": "O4", "CellValue": getFieldDetails('TermAPR')?.value?.trim() }
            ],
            "OutputCells": ["V11", "W11"],
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(financialData).then((data: ApiResponse) => {
            setIsACH(data[0].CellValue);
            setProductType(data[1].CellValue);
            updateLoaderCount(false);
            getSunlightPartnerTotalSaleAmount();
        }).catch(() => {
            updateLoaderCount(false);
        });
    }


    const updateOtherFields = (
        initialFields: FieldObject[],
        financialArr: string[],
        rebateMethodsList: DropdownItem[] = [],
    ) => {
        let solarQuoteFormFieldsTemp = initialFields;
        // Angular `updateOtherFields`: default first rebate payment type when not cloning.
        if (!confirmQuoteCellKey?.clonedQuoteDetails) {
            const rebateMethodField = solarQuoteFormFieldsTemp.find((f) => f.name === 'RebateMethod');
            const rebateOptions =
                rebateMethodsList.length > 0
                    ? rebateMethodsList
                    : (rebateMethodField?.options ?? []);
            if (rebateMethodField?.enable && rebateOptions.length > 0) {
                rebateMethodField.value = rebateOptions[0].Value;
            }
        }
        const pricingMethodArr = getFieldDetails('PricingMethod', solarQuoteFormFieldsTemp)?.options ?? [];
        const escalatorArr = getFieldDetails('Escalator', solarQuoteFormFieldsTemp)?.options ?? [];
        const termAPRArr = getFieldDetails('TermAPR', solarQuoteFormFieldsTemp)?.options ?? [];
        const downPaymentList = getFieldDetails('DownPayment', solarQuoteFormFieldsTemp)?.options ?? [];

        switch (confirmQuoteCellKey?.methord) {
            case 'PPA-EZ':
            case 'Prepaid PPA-EZ':
                if (pricingMethodArr.length > 0) {
                    solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(solarQuoteFormFieldsTemp, 'PricingMethod', '0');
                }
                if (localStorage.getItem('PPA_Requested_Quote_Created') != 'Not Interested') {
                    if (priceQuote.RequestedPPARateEscalator && escalatorArr.length > 0) {
                        let esc = priceQuote.RequestedPPARateEscalator.replace(/%/g, '');
                        if (esc == '0.0' || esc == '0.00') esc = '0';
                        let result = escalatorArr.filter((elem: any) => elem.Value.trim() == esc);
                        const Escalator =
                            matchQuoteEscalatorOption(
                                confirmQuoteCellKey?.ExcelOffer?.escalator,
                                escalatorArr,
                            ) ?? result[0].Value;
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(solarQuoteFormFieldsTemp, 'Escalator', Escalator);
                    }
                    if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                        const selectedPm = pricingMethodArr.find(
                            (a) => a.Value == getFieldDetails('PricingMethod', solarQuoteFormFieldsTemp)?.value,
                        );
                        const requestedRate = Number(priceQuote.RequestedPPARate);
                        const price = defaultPriceForPricingMethod(
                            selectedPm?.Text,
                            confirmQuoteCellKey?.ExcelOffer,
                            Number(priceQuote?.ProposedRate) || 0,
                        );
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'Price',
                            Number.isFinite(requestedRate) && requestedRate > 0
                                ? requestedRate
                                : price || getDefaultPricingValue(false, solarQuoteFormFieldsTemp),
                        );
                    }
                } else {
                    const Escalator =
                        matchQuoteEscalatorOption(
                            confirmQuoteCellKey?.ExcelOffer?.escalator,
                            escalatorArr,
                        ) ?? escalatorArr[0].Value;
                    if (escalatorArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(solarQuoteFormFieldsTemp, 'Escalator', Escalator);
                    }
                    if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'Price',
                            getDefaultPricingValue(false, solarQuoteFormFieldsTemp),
                        );
                    }
                }
                break;
            case 'Lease':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    const Escalator =
                        matchQuoteEscalatorOption(
                            confirmQuoteCellKey?.ExcelOffer?.escalator,
                            escalatorArr,
                        ) ?? escalatorArr[0].Value;
                    if (pricingMethodArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'PricingMethod',
                            pricingMethodArr[0].Value,
                        );
                    }
                    if (escalatorArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(solarQuoteFormFieldsTemp, 'Escalator', Escalator);
                    }
                    if (termAPRArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'TermAPR',
                            termAPRArr[0].Value,
                        );
                    }
                    if (downPaymentList.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'DownPayment',
                            downPaymentList[0].Value,
                        );
                    }
                }
                break;
            case 'Loan':
            case 'Loan Storage':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    const Escalator =
                        matchQuoteEscalatorOption(
                            confirmQuoteCellKey?.ExcelOffer?.escalator,
                            escalatorArr,
                        ) ?? escalatorArr[0].Value;
                    if (pricingMethodArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'PricingMethod',
                            pricingMethodArr[0].Value,
                        );
                    }
                    if (escalatorArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(solarQuoteFormFieldsTemp, 'Escalator', Escalator);
                    }
                    if (termAPRArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'TermAPR',
                            termAPRArr[0].Value,
                        );
                    }
                    if (downPaymentList.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'DownPayment',
                            downPaymentList[0].Value,
                        );
                    }
                    solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                        solarQuoteFormFieldsTemp,
                        'Price',
                        getDefaultPricingValue(false, solarQuoteFormFieldsTemp),
                    );
                }
                break;
            case 'PPA':
            case 'PPA Storage':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    const escalator =
                        matchQuoteEscalatorOption(
                            confirmQuoteCellKey?.ExcelOffer?.escalator,
                            escalatorArr,
                        ) ?? escalatorArr[0]?.Value;
                    if (pricingMethodArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'PricingMethod',
                            pricingMethodArr[0]?.Value,
                        );
                    }
                    if (escalatorArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'Escalator',
                            escalator,
                        );
                    }
                    if (termAPRArr.length > 0 && termAPRArr[0].Value) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'TermAPR',
                            termAPRArr[0].Value,
                        );
                    }
                    if (downPaymentList.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'DownPayment',
                            downPaymentList[0].Value,
                        );
                    }
                }
                break;
            case 'Cash Sale':
            case 'Cash Contract':
                if (pricingMethodArr.length > 0) {
                    solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                        solarQuoteFormFieldsTemp,
                        'PricingMethod',
                        pricingMethodArr.find((a) => a.Text === 'Desired PPW (Net)')?.Value,
                    );
                }
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    if (termAPRArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'TermAPR',
                            termAPRArr[0].Value,
                        );
                    }
                    if (downPaymentList.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'DownPayment',
                            downPaymentList[0].Value,
                        );
                    }
                }
                break;
            case 'Lease Storage':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    if (pricingMethodArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'PricingMethod',
                            pricingMethodArr[0].Value,
                        );
                    }
                    if (escalatorArr.length > 0) {
                        solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                            solarQuoteFormFieldsTemp,
                            'Escalator',
                            escalatorArr[0].Value,
                        );
                    }
                }
                break;
        }

        const PricingMethod = pricingMethodArr.find(
            (a) => a.Value == getFieldDetails('PricingMethod', solarQuoteFormFieldsTemp)?.value,
        );

        if (
            (confirmQuoteCellKey?.methord === 'PPA' || confirmQuoteCellKey?.methord === 'PPA Storage') &&
            !confirmQuoteCellKey?.clonedQuoteDetails &&
            !isLightreachPartner
        ) {
            const price = Number(priceQuote?.ProposedRate) || 0;
            solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                solarQuoteFormFieldsTemp,
                'Price',
                price,
            );
        }

        if (isGoodLeap) {
            const glProductName = lineMode === 'battery' ? 'Battery' : 'Solar';
            let financeRquest = priceQuote.FinanceRquestOptions.find(
                (ele: any) => ele.RequestedPartner.toLocaleLowerCase() == 'goodleap' && ele.Product_Type_Name == glProductName,
            );
            if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                    solarQuoteFormFieldsTemp,
                    'PricingMethod',
                    financeRquest?.Pricing_Method ? financeRquest.Pricing_Method : pricingMethodArr[0].Value,
                );
                if (!confirmQuoteCellKey?.ExcelOffer?.escalator && priceQuote.RequestedPPARateEscalator) {
                    let esc = priceQuote.RequestedPPARateEscalator.replace(/%/g, '');
                    if (esc == '0.0' || esc == '0.00') esc = '0';
                    let result = escalatorArr.find((elem: any) => elem.Value.trim() == esc);
                    solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                        solarQuoteFormFieldsTemp,
                        'Escalator',
                        !result ? escalatorArr[0].Value : priceQuote.RequestedPPARateEscalator.replace('%', ''),
                    );
                } else if (confirmQuoteCellKey?.ExcelOffer?.offerType) {
                    solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(
                        solarQuoteFormFieldsTemp,
                        'TermAPR',
                        confirmQuoteCellKey.ExcelOffer.offerType,
                    );
                }
            }
            if (confirmQuoteCellKey?.payload?.offerId) {
                let offerId = '';
                if (
                    existingOfferIdDetails &&
                    existingOfferIdDetails.FinanceMethod == financeMethod &&
                    ((financeMethod.includes('Loan') &&
                        getFieldDetails('TermAPR', solarQuoteFormFieldsTemp)?.value?.trim() ==
                            existingOfferIdDetails.TermAPR.trim()) ||
                        (!financeMethod.includes('Loan') &&
                            existingOfferIdDetails.Escalator ==
                                getFieldDetails('Escalator', solarQuoteFormFieldsTemp)?.value &&
                            !existingOfferIdDetails.isUtilityUpdated))
                ) {
                    offerId = existingOfferIdDetails.OfferId;
                    if (confirmQuoteCellKey) {
                        confirmQuoteCellKey.payload.offerId = existingOfferIdDetails.OfferId;
                    }
                    setOffer_Id(existingOfferIdDetails.OfferId);
                } else {
                    setOffer_Id(confirmQuoteCellKey?.payload?.offerId);
                    offerId = confirmQuoteCellKey?.payload?.offerId;
                }
                updateGoodLeapTotalSaleAmount(false, offerId, solarQuoteFormFieldsTemp);
            } else {
                goodLeapDropDownChange();
            }
        } else if (confirmQuoteCellKey?.partner == 'Sunlight') getSunlightPartnerValues();
        else updateSolarObject(false, solarQuoteFormFieldsTemp);

        solarQuoteFormFieldsTemp.forEach((ele: any) => {
            if (
                ele.enable &&
                getFieldDetails(ele?.field, solarQuoteFormFieldsTemp)?.value == null &&
                ele.dataType == 'Textbox'
            ) {
                solarQuoteFormFieldsTemp = updateSolarQuoteFormFieldsTemp(solarQuoteFormFieldsTemp, ele.field, 0);
            }
        });

        initFormFieldsRef.current = solarQuoteFormFieldsTemp;
        let finalFields = cloneFormFields(solarQuoteFormFieldsTemp);
        const selectedPmForTotal = pricingMethodArr.find(
            (a) => a.Value == getFieldDetails('PricingMethod', finalFields)?.value,
        );
        finalFields = cloneFormFields(syncPriceFieldsAfterExcelTotal(finalFields, selectedPmForTotal));
        const desiredPpwFieldFinal = finalFields.find((x) => x.name === 'DesiredPPWNet');
        if (desiredPpwFieldFinal?.enable) {
            const desiredValue = Number(desiredPpwFieldFinal.value ?? 0);
            desiredPpwFieldFinal.message =
                desiredValue < 2.5 ? 'The entered value should not be less than $2.5' : '';
        }
        setsolarQuoteFormFields(finalFields);
        if (confirmQuoteCellKey?.partner == 'Cash') {
            updateTotalSaleAmount(false, finalFields);
        }
        if (isEnfinPartner && lineMode !== 'battery') {
            const priceField = finalFields.find((field) => field.name === 'Price');
            if (priceField) {
                void fetchEnfinPricingCaps(priceField, finalFields, undefined, true, true);
            }
        }
    }

    const getTermAPR = (PricingMethod: any, data: any) => {
        let result = '';
        if (String(PricingMethod).includes('Loan'))
          result =
            (data.Term && data.Term != '' ? data.Term : 0) +
            ' years at '+
            (data.TermAPR && data.TermAPR != ''
              ? Number(data.TermAPR).toFixed(2)
              : 0) +
            '%';
        if (result == '')
          result =
            (data.TermAPR && data.TermAPR != ''
              ? Number(data.TermAPR).toFixed(1)
              : 0) + '%';
        return result;
      }

    const getSolarQuoteFormValues = (fm: string) => {
        if (!priceQuote) {
            setFormHydrating(false);
            return;
        }
        const installationState = priceQuote?.InstallationState ?? '';
        const excelUtilityCompanyName = priceQuote?.ExcelUtilityCompanyName ?? '';

        let selectedPricingValue = null;
        if (getFieldDetails('PricingMethod')?.enable) {
            selectedPricingValue = pricingMethodArr.find(a => (a.Value == getFieldDetails('PricingMethod')?.value || a.Text == getFieldDetails('PricingMethod')?.value));
        }
        const data = {
            "ExcelFileName": "Sunnova Financial Data",
            "SheetName": "SVA Financing Options",
            "InputValues": [{
                "Type": "String", "CellNumber": "B4", "CellValue": installationState
            },
            { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4(fm ?? financeMethod) },
            { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
            { "Type": "String", "CellNumber": "F4", "CellValue": fm ?? financeMethod },
            { "Type": "String", "CellNumber": "C8", "CellValue": eligibleProduct },
            { "Type": "String", "CellNumber": "E8", "CellValue": excelUtilityCompanyName },
            { "Type": "String", "CellNumber": "K4", "CellValue": selectedPricingValue?.Text }
            ], "OutputCells": ["G4", "N4", "H4", "L4", "I4", "G8", "T4", 'N11'],
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(data).then((response: ApiResponse) => { 
            let fields: string[] = response[0].CellValue.split('|');
            setFinancialArr(fields);

            let termAPRArrTemp: any[] = []
            let TermAPRArr: string[] = response[1].CellValue.split('|');
            let suffix = (confirmQuoteCellKey?.partner == 'Sunlight') ? ' w/ ACH' : '';
            TermAPRArr.forEach(ele => termAPRArrTemp.push({ Value: ele.trim() + suffix, Text: ele.trim() + suffix }));

            if (confirmQuoteCellKey?.partner == 'Sunlight') {
                let ACHTermAPRArr: string[] = response[7].CellValue.split('|');
                ACHTermAPRArr.forEach(ele => {
                    if (ele) termAPRArrTemp.push({ Value: ele.trim() + ' w/o ACH', Text: ele.trim() + ' w/o ACH' });
                });
            }
            if (confirmQuoteCellKey?.clonedQuoteDetails) {
                const clonedTermAPR = getFieldDetails('TermAPR')?.value;
                if (clonedTermAPR && !termAPRArrTemp.find(x => x.Value == clonedTermAPR)) termAPRArrTemp.push({ Value: clonedTermAPR, Text: clonedTermAPR });
            }
            if(fields.find(field => field.trim().toLocaleLowerCase() == 'downpayment')) {
                getDownpaymentList(financeMethod)
            } 

            let PricingMethodArr: string[] = response[2].CellValue.split('|');
            let PricingMethodvalArr: string[] = (response[4].CellValue.length > 0) ? response[4].CellValue.split('|') : [];
            let pricingMethods: any[] = [];
            for (let i = 0; i < PricingMethodArr.length; i++) {
                pricingMethods.push({
                    PricingValue: (PricingMethodvalArr[i]) ? PricingMethodvalArr[i].trim() : PricingMethodArr[i].trim(),
                    Text: PricingMethodArr[i].trim(),
                    Value: i+1
                })
            }
            setPricingMethodArr(pricingMethods);
            let escalatorArrTemp: any = [];
            let EscalatorArr: string[] = response[3].CellValue.split('|');
            EscalatorArr.forEach((ele) => escalatorArrTemp.push({ Value: ele.trim(), Text: ele.trim() }));
            if (escalatorArrTemp.length > 0) setEscolatorRange(escalatorArrTemp);
            let rebateMethods: string[] = response[5].CellValue.split('|');
            let rebateMethodsList: DropdownItem[] = [];
            rebateMethods.forEach((ele) => {
                const trimmed = ele.trim();
                if (trimmed) {
                    rebateMethodsList.push({ Value: trimmed, Text: trimmed });
                }
            });
            setRebateMethods(rebateMethodsList);
            if (confirmQuoteCellKey?.clonedQuoteDetails?.Rebate_Method) {
                const clonedRebate = String(confirmQuoteCellKey.clonedQuoteDetails.Rebate_Method).trim();
                if (
                    clonedRebate &&
                    !rebateMethodsList.some((opt) => opt.Value === clonedRebate)
                ) {
                    rebateMethodsList.push({ Value: clonedRebate, Text: clonedRebate });
                }
            }

            let solarQuoteFormFieldsTemp = [...solarQuoteFormFields];
            solarQuoteFormFieldsTemp.forEach(ele => {
                if (ele.name == "TermAPR") ele.options = termAPRArrTemp
                if (ele.name == "PricingMethod") ele.options = pricingMethods
                if (ele.name == "Escalator") ele.options = escalatorArrTemp
                if (ele.name == "RebateMethod") ele.options = rebateMethodsList
            });
            solarQuoteFormFieldsTemp = applyFinancialFieldAccess(
                fields,
                solarQuoteFormFieldsTemp,
                confirmQuoteCellKey?.partner,
            );

            enfinPricingFetchStartedRef.current = false;
            enfinInitLoadRef.current = true;
            initFormFieldsRef.current = solarQuoteFormFieldsTemp;

            if(confirmQuoteCellKey?.clonedQuoteDetails) {
                solarQuoteFormFieldsTemp.forEach(ele => {
                    if(ele.enable){
                        switch(ele.name){
                            case "TermAPR":
                                let Term = getTermAPR(confirmQuoteCellKey?.clonedQuoteDetails.Quote_FinanceMethod,
                                    {Term:confirmQuoteCellKey?.clonedQuoteDetails.Term,TermAPR:confirmQuoteCellKey?.clonedQuoteDetails.TermAPR})
                                // if (financePartner?.Text === ) {
                                //         Term = Term + ' w/ ACH';
                                // }
                                ele.value = String(Term);
                                break;
                            case "PricingMethod":
                                    const PricingMethod = confirmQuoteCellKey?.clonedQuoteDetails.Quote_PricingMethod;
                                    const PricingMethodId = pricingMethods.find(ele => ele.Text == PricingMethod)?.Value
                                    if (pricingMethods.length > 0) ele.value = PricingMethodId;
                                    else ele.value = ""
                                    applyPriceFieldFromPricingMethod(solarQuoteFormFieldsTemp, financialArr);
                                break;
                            case "Price":
                                if(confirmQuoteCellKey?.clonedQuoteDetails.Quote_PricingMethod == 'EPC')
                                    ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.PricingValue)?Number(confirmQuoteCellKey?.clonedQuoteDetails.PricingValue):0);
                                else if(confirmQuoteCellKey?.clonedQuoteDetails.Quote_PricingMethod == 'Desired PPW (Net)'){
                                    ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.Desired_PPW_Net);
                                } else ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Solar_Rate)?confirmQuoteCellKey?.clonedQuoteDetails.Solar_Rate:0);
                                break;        
                            case "Escalator":
                                    ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Escalator)?confirmQuoteCellKey?.clonedQuoteDetails.Escalator:0);
                                    applyPriceFieldFromPricingMethod(solarQuoteFormFieldsTemp, financialArr);
                                break;
                            // case "Rate":

                            // case "EPC":

                            // case "PPW":

                            case "DownPayment":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.DownPayment)?confirmQuoteCellKey?.clonedQuoteDetails.DownPayment:0);
                                break;
                            case "DesiredPPWNet":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.Desired_PPW_Net);
                                break;
                            case "Deposit_Amount":
                                ele.value = Number(confirmQuoteCellKey?.clonedQuoteDetails.Deposit_Amount);
                                break;
                            case "RebateMethod":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Rebate_Method)?confirmQuoteCellKey?.clonedQuoteDetails.Rebate_Method:"");
                                break;
                            case "RebateValue":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Rebate)?confirmQuoteCellKey?.clonedQuoteDetails.Rebate:0);
                                break;
                            case "RebateAmountPerWatt":
                                ele.value = String(
                                    confirmQuoteCellKey?.clonedQuoteDetails.Rebate_Amount_PerWatt ??
                                        confirmQuoteCellKey?.clonedQuoteDetails.Rebate ??
                                        0,
                                );
                                break;
                            case "Tax":

                            case "AdderCost":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Apr)?confirmQuoteCellKey?.clonedQuoteDetails.Apr:0);
                                break;
                            case "AdderPrice":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Adder_Price)?confirmQuoteCellKey?.clonedQuoteDetails.Adder_Price:0);
                                break;
                            case "UponCommencementOfPhysicalInstall":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.AmountOnPhysicalInstallStart);
                                break;
                            case "UponCompletionOfPhysicalInstall":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.AmountOnPhysicalInstallCompletion);
                                break;
                            case "UponCompletionOfTheContract":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.AmountOnContractCompletion);
                                break;
                            default:
                                break;
                        }
                    }
                })
            }
            setsolarQuoteFormFields(solarQuoteFormFieldsTemp);
            updateOtherFields(solarQuoteFormFieldsTemp, fields, rebateMethodsList);
            initFormFieldsRef.current = null;
            // Release Excel loader; GetEnfinPricing keeps its own loader token when active.
            enfinInitLoadRef.current = false;
            setFormHydrating(false);
            updateLoaderCount(false);
        }).catch(() => {
            setFormHydrating(false);
            updateLoaderCount(false);
        });
    }


    const getFinanceMethod = () => {
        if (!priceQuote) return;
        const installationState = priceQuote?.InstallationState ?? '';
        const excelUtilityCompanyName = priceQuote?.ExcelUtilityCompanyName ?? '';
        if (confirmQuoteCellKey?.partner === 'Cash') {
            setsolarQuoteFormFields((prev) =>
                applyFinancialFieldAccess([], prev, confirmQuoteCellKey?.partner),
            );
        }
        const data = {
            "ExcelFileName": "Sunnova Financial Data",
            "SheetName": "SVA Financing Options",
            "InputValues": [{
                "Type": "String", "CellNumber": "B4", "CellValue": installationState
            },
            { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4(confirmQuoteCellKey?.methord as string) },
            { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
            { "Type": "String", "CellNumber": "C8", "CellValue": eligibleProduct },
            { "Type": "String", "CellNumber": "E8", "CellValue": excelUtilityCompanyName }
            ], "OutputCells": ["E4"]
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(data).then((response: ApiResponse) => {
            setFinanceMethod(response[0].CellValue);
            if (confirmQuoteCellKey) confirmQuoteCellKey.methord = response[0].CellValue;
            getSolarQuoteFormValues(response[0].CellValue);
            // Release finance-method Excel loader; getSolarQuoteFormValues owns the next cycle.
            updateLoaderCount(false);
        }).catch(() => {
            setFormHydrating(false);
            updateLoaderCount(false);
        });
    }


    const getSliderRateNote = (field: any): string => {
        if (field?.name !== 'Price' || !field?.range?.nzValue) return '';
        const currentRate = Number(getFieldDetails(field.name)?.value);
        const targetRate = Number(field.range.targetRate ?? field.range.nzValue);
        const minRate = Number(field.range.nzMin);
        const maxRate = Number(field.range.nzMax);
        const escalationRate = field.range.escalationRate || getFieldDetails('Escalator')?.value;
        if (isNaN(currentRate) || isNaN(targetRate) || isNaN(minRate) || isNaN(maxRate)) return '';
        if (currentRate < minRate || currentRate > maxRate) {
            return ` Starting rate is out of range of the pricing the finance partner allows. Please adjust the utility information or switch finance partners.`;
        }
        if (currentRate === targetRate) {
            return ` current rate matches target rate of <b>$${targetRate}</b> at <b>${escalationRate}% escalator</b>`;
        }
        const direction = currentRate < targetRate ? 'below' : 'At or above';
        return ` current rate ${direction} target rate of <b>$${targetRate}</b> at <b>${escalationRate}% escalator</b>`;
    };

    const getSliderRateNoteColor = (field: any): string => {
        if (field?.name !== 'Price' || !field?.range?.nzValue) return '';
        const currentRate = Number(getFieldDetails(field.name)?.value);
        const targetRate = Number(field.range.targetRate ?? field.range.nzValue);
        const minRate = Number(field.range.nzMin);
        const maxRate = Number(field.range.nzMax);
        if (isNaN(currentRate) || isNaN(targetRate) || isNaN(minRate) || isNaN(maxRate)) return '';

        if (currentRate < minRate || currentRate > maxRate) return 'red';
        return currentRate < targetRate ? '#d4a900' : 'green';
    };

    useEffect(() => {
        if (!confirmQuoteCellKey) return;
        const columnOk =
            lineMode === 'battery'
                ? columnIncludesBattery(confirmQuoteCellKey.product)
                : columnIncludesSolar(confirmQuoteCellKey.product);
        if (!columnOk) {
            setFormHydrating(false);
            return;
        }
        setFormHydrating(true);
        amountDraftRef.current = {};
        pricingDetailsFromExcelRef.current = null;
        setsolarQuoteFormFields(JSON.parse(JSON.stringify(solarFields)));
        setEnfinPricingOfferDetails([]);
        setEnfinBatteryPricingOfferDetails([]);
        setEnfinPricingLoaded(false);
        setEnfinBatteryPricingLoaded(false);
        enfinPricingLoadGenRef.current += 1;
        enfinInitLoadRef.current = false;
        enfinPricingFetchStartedRef.current = false;
        enfinEmptyForceRef.current = false;
        enfinLoaderIncrementedRef.current = false;
        initFormFieldsRef.current = null;

        const partner = confirmQuoteCellKey.partner || '';
        const isSunnova = /^sunnova$/i.test(partner.trim());

        if (lineMode === 'battery' && isSunnova) {
            const c4Batt = batteryCalculatorC4FromColumn(confirmQuoteCellKey.product);
            const battPayload = {
                ExcelFileName: 'Battery Calculator',
                SheetName: 'Battery IO',
                InputValues: [
                    { Type: 'String', CellNumber: 'C3', CellValue: priceQuote?.InstallationState ?? '' },
                    { Type: 'String', CellNumber: 'C4', CellValue: c4Batt },
                    { Type: 'String', CellNumber: 'C7', CellValue: getExcelFinancePartner(partner) },
                ],
                OutputCells: ['F14', 'F15'],
            };
            masterServices.cascadingDropDown(battPayload).then((out: ApiResponse) => {
                if (!out?.[0]) return;
                const methods = String(out[0].CellValue || '')
                    .split('|')
                    .map((s) => s.trim())
                    .filter(Boolean);
                const fromGrid = (confirmQuoteCellKey?.methord || '').trim();
                const meth =
                    fromGrid && methods.some((m) => m === fromGrid)
                        ? fromGrid
                        : methods[0] || fromGrid;
                setFinanceMethod(meth);
                getSolarQuoteFormValues(meth);
            }).catch(() => { });
            return;
        }

        if (!confirmQuoteCellKey?.methord || confirmQuoteCellKey.methord == 'CASH') getFinanceMethod();
        else {
            if (confirmQuoteCellKey?.partner === 'Cash') {
                setsolarQuoteFormFields((prev) =>
                    applyFinancialFieldAccess([], prev, confirmQuoteCellKey?.partner),
                );
            }
            setFinanceMethod(confirmQuoteCellKey?.methord);
            getSolarQuoteFormValues(confirmQuoteCellKey?.methord);
        }

        if (normalizePartnerToken(partner) === 'enfin' && lineMode !== 'battery') {
            const priceField =
                solarQuoteFormFields.find((field) => field.name === 'Price') ??
                ({ name: 'Price', label: 'Price' } as FieldObject);
            void fetchEnfinPricingCaps(priceField, solarQuoteFormFields, undefined, true, true);
        }
    }, [confirmQuoteCellKey, lineMode]);

    /**
     * When EPC min is (system kW * 1000 * 2.5), the first `updateOtherFields` may run before `EPCCalc.SystemSizeValue`
     * is populated, so min is 0 and the Price under-min message is skipped. Re-validate when system size lands.
     */
    useEffect(() => {
        if (!isGoodLeap) return;
        const sys = Number(priceQuote?.EPCCalc?.SystemSizeValue) || 0;
        if (sys <= 0) return;
        setsolarQuoteFormFields((prev) => {
            const minEpc = getDefaultPricingValue(true, prev);
            if (minEpc <= 0) return prev;
            const selected = pricingMethodArr.find(
                (a) => a.Value == prev.find((f) => f.name === 'PricingMethod')?.value,
            );
            if (!selected) return prev;
            const isEpc = selected.PricingValue === 'EPC to Dealer' || selected.Text === 'EPC';
            if (!isEpc) return prev;
            const priceEl = prev.find((f) => f.name === 'Price');
            if (!priceEl?.enable) return prev;
            const priceNum = Number(priceEl.value) || 0;
            return prev.map((el) => {
                if (el.name !== 'Price') return el;
                if (priceNum < minEpc) {
                    return { ...el, message: 'The entered value should not be less than $' + minEpc };
                }
                if (el.message?.startsWith('The entered value should not be less than $')) {
                    return { ...el, message: '' };
                }
                return el;
            });
        });
    }, [isGoodLeap, confirmQuoteCellKey?.clonedQuoteDetails, priceQuote?.EPCCalc?.SystemSizeValue, pricingMethodArr]);

    const downPaymentField = getFieldDetails('DownPayment');
    const desiredPpwField = getFieldDetails('DesiredPPWNet');
    useEffect(() => {
        if (!desiredPpwField?.enable) return;
        const message = 'The entered value should not be less than $2.5';
        const desiredValue = Number(desiredPpwField.value ?? 0);
        const nextMessage = desiredValue < 2.5 ? message : '';
        if ((desiredPpwField.message ?? '') === nextMessage) return;
        let changed = false;
        const nextFields = solarQuoteFormFields.map((el) => {
            if (el.name !== 'DesiredPPWNet') return el;
            const current = el.message ?? '';
            if (current === nextMessage) return el;
            changed = true;
            return { ...el, message: nextMessage };
        });
        if (!changed) return;
        setsolarQuoteFormFields(nextFields);
        updateSolarObject(false, nextFields);
    }, [desiredPpwField?.enable, desiredPpwField?.value, desiredPpwField?.message, solarQuoteFormFields]);
    const downPaymentUsesOptions =
        !!downPaymentField?.options &&
        Array.isArray(downPaymentField.options) &&
        downPaymentField.options.length > 0;

    /** Good Leap / Sunlight: desired PPW (net) and down payment are pipeline-driven — hide rail editors; keep in main grid when enabled. */
    const solarRailHideDownPaymentAndDesiredPpw =
        lineMode === 'solar' && (partnerNorm === 'goodleap' || partnerNorm === 'sunlight');

    const sectionTitle = lineMode === 'battery' ? 'Battery' : 'Solar';
    const solarHeaderStats: GqSectionStat[] = [];
    const solarTotalAdders = Number(getFieldDetails('AdderCost')?.value ?? 0) || 0;
    /** Angular `solar-detail-form`: LightReach / Enfin `app-solar-total` bindings. */
    const headerGrossOrTotalAmount = isLightreachPartner
        ? Number(lightreachDerived.lightreachTotalSaleAmount) + solarTotalAdders
        : isEnfinPartner
            ? (confirmQuoteCellKey?.productType === 'SB'
                ? enfinDerived.enfinGrossSalesPrice
                : enfinDerived.enfinTotalSaleAmount)
            : totalSaleAmount;

    if (isLightreachPartner) {
        solarHeaderStats.push({
            label: 'Total adder amount',
            value: `$${solarTotalAdders.toFixed(2)}`,
        });
        solarHeaderStats.push({
            label: 'Gross sales price',
            value: `$${Number(headerGrossOrTotalAmount).toFixed(2)}`,
            emphasize: true,
        });
    } else if (headerGrossOrTotalAmount > 0) {
        if (confirmQuoteCellKey?.partner == 'Sunnova') {
            solarHeaderStats.push({
                label: 'Total sales amount',
                value: `$${Number(headerGrossOrTotalAmount).toFixed(2)}`,
            });
        } else {
            solarHeaderStats.push({
                label: 'Gross sales price',
                value: `$${Number(headerGrossOrTotalAmount).toFixed(2)}`,
                emphasize: true,
            });
        }
        if (financeMethod.includes('Loan')) {
            solarHeaderStats.push({
                label: 'Financed amount',
                value: `$${Number(financedAmount).toFixed(2)}`,
            });
        }
    } else if (confirmQuoteCellKey?.partner != 'IGS') {
        solarHeaderStats.push({
            label: 'Totals',
            value: formHydrating ? 'Loading…' : 'Pending',
        });
    }

    const selectedPricingMethod = pricingMethodArr.find(
        (p) => p.Value == getFieldDetails('PricingMethod')?.value,
    );
    const normalizedPricingMethodLabel = (selectedPricingMethod?.Text ?? '').trim().toLowerCase();
    const priceDecimalsLimit = PRICING_METHOD_LABELS_EXTRA_PRICE_DECIMALS.includes(
        normalizedPricingMethodLabel,
    )
        ? 3
        : DEFAULT_AMOUNT_DECIMALS_LIMIT;

    const pricingMethodDisplayText = (selectedPricingMethod?.Text ?? '').trim();
    const priceAdjustmentSectionLabel =
        pricingMethodDisplayText.length > 0 ? `Adjustments ${pricingMethodDisplayText}` : 'Adjustments';

    const formBlocked =
        formHydrating || !solarQuoteFormFields.some((field) => field.enable);

    return (
        <div
            className={`obd-modal__body obd-roof-quote-details__container--split obd-gq-section${
                formBlocked ? ' obd-gq-section--loading' : ''
            }`}
            style={{ padding: 0 }}
            aria-busy={formBlocked}
        >
            {formBlocked ? (
                <div className="obd-generate-quote-form-cover" role="status" aria-live="polite">
                    <CircleNotch size={22} weight="bold" className="pci-ai-designs__spinner" />
                    <span>Loading quote form…</span>
                </div>
            ) : null}
            <GenerateQuoteSectionHead
                title={sectionTitle}
                stats={solarHeaderStats}
                ariaLabel={`${sectionTitle} quote totals`}
            />
            <div className="obd-gq-section-body">
            <div
                className={`obd-generate-quote-split${lineMode === 'battery' || lineMode === 'solar' ? ' obd-generate-quote-split--no-aside' : ''}`}
            >
                <div className="obd-generate-quote-split__forms">
                    <div className="obd-gq-product-line">
                        {confirmQuoteCellKey?.partner} — {confirmQuoteCellKey?.product}
                    </div>
                <div className="obd-utility__fields obd-generate-quote-form-fields">
                    <div className="obd-utility__field">
                        <label className="obd-utility__field-label">Finance Method</label>
                        <OBDSelect
                            className="obd-utility__select"
                            value={financeMethod}
                            disabled
                            options={[{ value: financeMethod, label: financeMethod }]}
                        />
                    </div>
                    {/* Amount & Price-as-Slider: third arg `true` on change defers totals/parent sync until blur. Slider+Currency share one Price via setSolarQuoteFromValues. */}
                    {solarQuoteFormFields
                        .filter((field) => field.enable)                        
                        .map((field: any) => {
                            const isPriceSlider = field.type === 'Slider' && field.name === 'Price';
                            let nzMin: number | undefined;
                            let nzMax: number | undefined;
                            let price = 0;
                            let handleSliderChange: (value: number | number[]) => void = () => {};
                            let handleSliderCurrencyChange: (value: string | undefined) => void = () => {};

                            if (isPriceSlider) {
                                const nMin = Number(field.range?.nzMin);
                                const nMax = Number(field.range?.nzMax);
                                nzMin = Number.isFinite(nMin) ? nMin : undefined;
                                nzMax = Number.isFinite(nMax) ? nMax : undefined;
                                // Single source of truth for the slider handle: `field.value`, same as the adjustment
                                // CurrencyInput. Preferring `range.nzValue` let the track drift from the stored price
                                // when `nzValue` was out of sync; bounds still come from `range`.
                                const rawPrice = parseAmountDisplayString(String(field.value ?? '0'));
                                price = clampPriceToSliderRange(
                                    Number.isFinite(rawPrice) ? rawPrice : 0,
                                    nzMin,
                                    nzMax,
                                );

                                handleSliderChange = (value: number | number[]) => {
                                    const v = Array.isArray(value) ? value[0] : value;
                                    const clamped = clampPriceToSliderRange(Number(v), nzMin, nzMax);
                                    setSolarQuoteFromValues(field.name, clamped);
                                };

                                handleSliderCurrencyChange = (value: string | undefined) => {
                                    if (value === undefined) return;
                                    const v = value ?? '';
                                    amountDraftRef.current[field.name] = v;
                                    if (v.trim() === '') {
                                        setSolarQuoteFromValues(field.name, v, true);
                                        return;
                                    }
                                    const parsed = parseAmountDisplayString(v);
                                    if (!Number.isFinite(parsed)) {
                                        setSolarQuoteFromValues(field.name, v, true);
                                        return;
                                    }
                                    const clamped = clampPriceToSliderRange(parsed, nzMin, nzMax);
                                    setSolarQuoteFromValues(field.name, String(clamped), true);
                                };
                            }

                            return (
                        <div className="obd-utility__field" key={field.name}>
                            <label className="obd-utility__field-label">{field.label}</label>
                            {field.type === 'amount' && (

                                <CurrencyInput
                                    className="obd-utility__input"
                                    name={field.name}
                                    value={
                                        amountDraftRef.current[field.name] ??
                                        (field.value === undefined || field.value === null || String(field.value).trim() === ''
                                            ? ''
                                            : String(field.value))
                                    }
                                    onKeyDown={(e) => validateFieldValue(e, field)}
                                    onValueChange={(value) => {
                                        const v = value ?? '';
                                        amountDraftRef.current[field.name] = v;
                                        setSolarQuoteFromValues(field.name, v, true);
                                    }}
                                    onBlur={() => {
                                        setSolarQuoteFromValues(
                                            field.name,
                                            amountDraftRef.current[field.name] ?? String(field.value ?? ''),
                                            false,
                                        );
                                        delete amountDraftRef.current[field.name];
                                    }}
                                    prefix="$"
                                    decimalsLimit={
                                        field.name === 'Price'
                                            ? priceDecimalsLimit
                                            : DEFAULT_AMOUNT_DECIMALS_LIMIT
                                    }
                                    allowNegativeValue={false}
                                />
                            )}
                            {field.type === 'dropdown' && (
                                <OBDSelect
                                    className="obd-utility__select"
                                    disabled={!field.enable}
                                    placeholder={
                                        field?.name === 'PricingMethod' || field?.name === 'RebateMethod'
                                            ? undefined
                                            : 'Select'
                                    }
                                    value={field.value === '' ? undefined : field.value}
                                    onChange={(value) =>
                                        setSolarQuoteFromValues(
                                            field.name,
                                            value,
                                            false,
                                            field.name === 'PricingMethod' ||
                                                field.name === 'Escalator' ||
                                                (field.name === 'TermAPR' && isEnfinPartner),
                                        )
                                    }
                                    options={(field?.options ?? []).map((option: any) => ({
                                        value: option.Value,
                                        label: option.Text,
                                    }))}
                                />
                            )}
                            {isPriceSlider && (
                                <div className="obd-price-slider-row">
                                    <div className="obd-price-slider-row__track">
                                        <Slider
                                            className="obd-price-slider"
                                            style={getSliderCssVars(field.range?.style)}
                                            min={nzMin}
                                            max={nzMax}
                                            step={field.range?.nzStep}
                                            marks={field.range?.nzMarks}
                                            value={price}
                                            onChange={handleSliderChange}
                                            tooltip={{ formatter: () => `${price}` }}
                                            disabled={!nzMin || !nzMax}
                                        />
                                        { getSliderRateNote(field) && (
                                            <p className="slider-rate-note">
                                                <span style={{ color: getSliderRateNoteColor(field)}}>
                                                    <b>{ getFieldDetails(field.name)?.value } </b> 
                                                </span>
                                                <span dangerouslySetInnerHTML={{ __html:getSliderRateNote(field)}}></span>
                                            </p>
                                        )}   
                                        
                                    </div>                           
                                    <div className="obd-price-adjustment-container">
                                        <label
                                            className="obd-utility__field-label"
                                            htmlFor="obd-solar-price-adjustment-input"
                                        >
                                            {priceAdjustmentSectionLabel}
                                        </label>
                                        <CurrencyInput
                                            id="obd-solar-price-adjustment-input"
                                            className="obd-utility__input obd-price-slider-row__input"
                                            name={field.name}
                                            value={
                                                amountDraftRef.current[field.name] ??
                                                (field.value === undefined || field.value === null || String(field.value).trim() === ''
                                                    ? ''
                                                    : String(field.value))
                                            }
                                            onKeyDown={(e) => validateFieldValue(e, field)}
                                            onValueChange={handleSliderCurrencyChange}
                                            onBlur={() => {
                                                const raw =
                                                    amountDraftRef.current[field.name] ?? String(field.value ?? '');
                                                const n = parseAmountDisplayString(raw);
                                                // const clamped = Number.isFinite(n) && raw.trim() !== ''
                                                //     ? clampPriceToSliderRange(n, nzMin, nzMax)
                                                //     : price;
                                                setSolarQuoteFromValues(field.name, String(n), false);
                                                delete amountDraftRef.current[field.name];
                                            }}
                                            prefix="$"
                                            decimalsLimit={3}
                                            allowNegativeValue={true}
                                        />

                                    </div>
                                </div>
                            )}
                            {field.message && <span className='validation-message'>{field.message} </span>}
                        </div>
                            );
                        })}
                </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default SolarQuoteDetails;